<template>
  <div ref="outer" class="scenario-analysis container">
    <!-- control variables -->
    <div class="header-container">
      <div class="flex justify-content-between">
        <h1>Analysis</h1>

        <div class="analysis-modes">
          <div class="col-12 my-3 flex align-items-center">
            <span>
              <strong> Result Method: </strong>
            </span>
            <span
              title="Indiscrete mode will run a full cross product of all selected control variables with all the possible steps."
            >
              <span class="ml-1 mr-2">Indiscrete</span>
              <RadioButton
                :model-value="scenarioAnalysisMode"
                name="indiscrete'"
                value="indiscrete"
                @update:model-value="setScenarioAnalysisMode($event)"
              />
            </span>

            <span
              title="Discrete mode will only run a cross product with each variable incrementing based only on the first selected control variable's steps."
            >
              <span class="ml-1 mr-2">Discrete</span>
              <RadioButton
                :model-value="scenarioAnalysisMode"
                name="discrete'"
                value="discrete"
                @update:model-value="setScenarioAnalysisMode($event)"
              />
            </span>
          </div>
        </div>

        <div class="flex align-items-center">
          <div v-if="exportError">
            <span class="error">Export error: {{ exportError }}</span>
          </div>
          <div
            v-if="isExporting"
            :class="[
              'export-progress',
              {
                animate:
                  selectedWatchVariables.length > selectedControls.length &&
                  selectedControls.values.length > 250
              }
            ]"
          >
            <div class="progress-labels">
              <div class="progress-percent">
                {{ exportProgress + "%" }}
              </div>
              <div class="progress-completed">
                {{ exportFinished.toLocaleString() }} / {{ exportTotal }}
              </div>
            </div>
            <div class="progressbar-wrapper">
              <ProgressBar :value="exportProgress">{{ "" }}</ProgressBar>
            </div>
            <Button class="p-button-danger mr-2" @click="cancelExport">
              Cancel
            </Button>
          </div>

          <div v-else>
            <Button
              :disabled="!analysisData.length"
              class="p-button-info export-button"
              label="Export"
              @click="startExport"
            />
            <Dropdown
              v-model="exportFileType"
              append-to="body"
              :options="exportFileTypes"
            />
          </div>
        </div>
      </div>
      <Divider class="my-1" align="left">Control Variable</Divider>
      <div v-for="(control, i) in controls" :key="`control-${i}`" class="grid">
        <div class="col-6">
          <AddRemoveButtons
            class="add-remove-button-group"
            :hide-remove="i === 0"
            :hide-add-above="true"
            :hide-add-below="i < controls.length - 1 || !canAddAnotherControl"
            @add-below="addControl"
            @remove="removeControl(i)"
          >
            <template #middle-content>
              <Dropdown
                v-model="control.selectedControl"
                class="analysis-row-content"
                :options="
                  controlVariables.filter(
                    v =>
                      !selectedControls.some(
                        ({ selectedControl }, j) =>
                          selectedControl.label === v.label && i !== j
                      )
                  )
                "
                option-label="label"
                :placeholder="`Control Variable #${i + 1}`"
              >
                <template #value="{ value, placeholder }">
                  <template v-if="value">
                    <div class="control-dropdown-label">
                      <span>{{ value.label }}</span>
                      <div class="flex align-items-center">
                        <span class="body-4 mr-1">Current Value:</span>
                        <span class="numeral-4">
                          {{
                            getConvertedValue(value).toFixed(
                              value.label === "PI" ? 4 : 2
                            )
                          }}
                        </span>
                      </div>
                    </div>
                  </template>
                  <template v-else>{{ placeholder }}</template>
                </template>
              </Dropdown>
            </template>
          </AddRemoveButtons>
        </div>
        <ConverterInput
          v-model="control.min"
          class="col"
          :disabled="!control.selectedControl"
          :label="`${control.selectedControl?.label || ''} Min`.trim()"
          :base-unit="control.selectedControl?.base || GenericUnits.Generic"
          :target-unit="control.selectedControl?.target"
          :model="control.selectedControl?.model || []"
          mode="write"
          :updateScenario="false"
          @update:unit-selected="
            (value: ConvertableUnit | undefined) =>
              onUnitSelected(control.selectedControl?.unitType, value)
          "
        />
        <ConverterInput
          v-model="control.max"
          class="col"
          :disabled="!control.selectedControl"
          :label="`${control.selectedControl?.label || ''} Max`.trim()"
          :base-unit="control.selectedControl?.base || GenericUnits.Generic"
          :target-unit="control.selectedControl?.target"
          :model="control.selectedControl?.model || []"
          mode="write"
          :updateScenario="false"
        />
        <div class="col">
          <LabelledInput
            v-if="useFullCrossProduct || i === 0"
            :id="`steps-input-${i}`"
            v-model="control.steps"
            :disabled="!control.selectedControl"
            :label="
              control.selectedControl?.label === focusedControlLabel
                ? `Steps: (max ${maxStepsExcludeFocusedInput})`
                : 'Steps:'
            "
            type="number"
            :max="maxStepsExcludeFocusedInput"
            title="For iterations, n+1 values are generated (e.g., enter 59 for 60 results)"
            :class="{
              'stepper-input': true,
              'stepper-available': i === 0 || useFullCrossProduct
            }"
            @blur="focusedControlLabel = null"
            @focus="
              focusedControlLabel = control.selectedControl?.label || null
            "
          />
          <div class="reverse-checkbox">
            <Checkbox
              v-model="control.reversed"
              :binary="true"
              :disabled="!control.selectedControl"
            />
            <label>Invert</label>
          </div>
        </div>
      </div>
    </div>
    <!-- watch variables -->
    <div class="data-container">
      <Divider class="my-0" align="left">Watch Variables</Divider>
      <div class="data-buttons">
        <FAToggleButton v-model="showSettings">
          <icon :icon="['far', 'gears']" />
        </FAToggleButton>
        <Button
          class="p-button-danger p-button-outlined"
          label="Clear"
          @click="resetForm"
        />
      </div>
      <div ref="watchContainerRef" class="watch-container">
        <DataTable
          v-model:selection="finalWatchVariables"
          class="watch-table"
          selection-mode="multiple"
          :meta-key-selection="false"
          :value="watchVariables"
          :frozen-value="selectedControls.map(c => c.selectedControl)"
        >
          <div class="watch-variables">
            <Column selection-mode="multiple" header-style="max-width: 3em" />
            <Column field="label" header="Watch Variables" />
          </div>
        </DataTable>
      </div>
      <!-- chart were you see the analysis -->
      <div
        ref="analysisContainerRef"
        :class="['analysis-container', { scrollable: isScrollable }]"
      >
        <div v-if="showSettings" class="settings-container">
          <div class="input-option-group">
            <p class="body-2">Liquid Input</p>

            <div
              v-for="option in wellTestLiquidInputOptions?.well_test_liquid_input_options"
              :key="`liquid-input-options-${option.value}`"
            >
              <RadioButton
                v-model="settings.liquidInputType"
                :input-id="`liquid-input-options-${option.value}`"
                name="liquid-input-options"
                :value="option.value"
              />

              <label :for="`liquid-input-options-${option.value}`">
                {{ option.comment }}
              </label>
            </div>
          </div>
          <div class="input-option-group">
            <p class="body-2">Gas Input</p>
            <div
              v-for="option in wellTestGasInputOptions?.well_test_gas_input_options"
              :key="`gas-input-options-${option.value}`"
            >
              <RadioButton
                v-model="settings.gasInputType"
                :input-id="`gas-input-options-${option.value}`"
                name="gas-input-options"
                :value="option.value"
              />
              <label :for="`gas-input-options-${option.value}`">
                {{ option.comment }}
              </label>
            </div>
          </div>
          <div class="flex gap-4 align-items-end">
            <div
              v-for="option in predictionFunctionOptions"
              :key="option.comment"
            >
              <Button
                :disabled="option.disabled"
                :class="{
                  'p-button-info': settings.predictionFunction === option.value
                }"
                @click="
                  settings.predictionFunction === option.value
                    ? (settings.predictionFunction = null)
                    : (settings.predictionFunction = option.value)
                "
              >
                {{ option.comment }}
              </Button>
            </div>
          </div>
        </div>
        <div
          v-if="isReady && analysisDisplayData.length < 3"
          class="table-loader"
        >
          <ProgressSpinner />
        </div>
        <div ref="scrollRef" class="scroller">
          <div ref="scrollInnerRef" class="scroller-inner"></div>
        </div>
        <DataTable
          v-if="isReady"
          ref="analysisTableRef"
          class="analysis-table"
          :value="analysisDisplayData"
        >
          <Column
            v-for="variable in finalWatchVariables"
            :key="variable.label"
            :field="variable.label"
            :header="formatVariableHeader(variable)"
          >
            <template #body="{ data }">
              <template v-if="data && Number.isNaN(data[variable.label])">
                <span>N/A</span>
              </template>
              <template
                v-else-if="
                  data && (data[variable.label] || data[variable.label] === 0)
                "
              >
                <span class="numeral-3"> {{ data[variable.label] }}</span>
              </template>
              <template v-else-if="data.isFirst">
                <div
                  class="first"
                  :style="{
                    height: `${scrollTop}px`
                  }"
                ></div>
              </template>
              <template v-else-if="data.isLast">
                <div class="last"></div>
              </template>
              <template v-else>
                <Skeleton height="18.4px" />
              </template>
            </template>
          </Column>
        </DataTable>
        <div v-else class="analysis-placeholder">
          <h4>Select some data to analyze.</h4>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { ComputedRef, UnwrapNestedRefs } from "vue";
import {
  watchEffect,
  nextTick,
  onMounted,
  onUnmounted,
  computed,
  reactive,
  ref,
  watch,
  onBeforeMount
} from "vue";

import type { ConverterInputProps } from "@/components/types";
import type { ScenarioCalculations } from "@/composables/scenarioCalculations/calculationTypes";
import type { DestructuredScenario } from "@/composables/useDestructureScenario";
import {
  scenarioKey,
  sizingKey,
  wellPropertiesFormKey
} from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";

/**
 * Xlsx mini build is untyped so ts-ignore is added to satisfy compiler
 * https://github.com/SheetJS/sheetjs/issues/1999
 *
 * Mini build is used because it is 212KB vs 1.2MB
 * for the whole build
 */
/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-ignore
import XLSX from "xlsx/dist/xlsx.mini.min.js";

import { useDebounceFn, watchDebounced, watchThrottled } from "@vueuse/core";

import { GasFlowRateUnits, LiquidFlowRateUnits } from "@/libs/units/flowRate";
import { FrequencyUnits } from "@/libs/units/frequency";
import { LiquidRatioUnits } from "@/libs/units/liquidRatio";
import { PressureUnits } from "@/libs/units/pressure";
import { LargeLengthUnits } from "@/libs/units/length";
import { ProductivityIndexUnits } from "@/libs/units/productivityIndex";
import { PercentageUnits } from "@/libs/units/percentage";
import type { ConvertableUnit } from "@/libs/units";
import { convert } from "@/libs/units";
import { PowerUnits } from "@/libs/units/power";
import { GenericUnits } from "@/libs/units/generic";
import { TemperatureUnits } from "@/libs/units/temperature";
import { ApparentPowerUnits } from "@/libs/units/apparentPower";
import { SpeedUnits } from "@/libs/units/speed";
import { VoltageUnits } from "@/libs/units/voltage";
import { CurrentUnits } from "@/libs/units/current";
import { extrapolateFlowRates } from "@/libs/calculations/productionRates";
import {
  asyncTimeout,
  injectStrict,
  removeArrayElement,
  formatNumber
} from "@/utils/utils";
import type {
  Well_Test_Gas_Input_Options_Enum,
  Well_Test_Liquid_Input_Options_Enum
} from "gql/api";
import {
  Override_Options_Enum,
  Sizing_Pumping_Type_Enum,
  useWellTestGasInputOptionsQuery,
  useWellTestLiquidInputOptionsQuery
} from "gql/api";

import ConverterInput from "@/components/ConverterInput.vue";
import LabelledInput from "@/components/LabelledInput.vue";

import { useScenarioCalculations } from "@/composables/scenarioCalculations/useScenarioCalculations";
import { useScenarioUnits } from "@/composables/units/useScenarioUnits";

import useDestructureScenario from "@/composables/useDestructureScenario";
import { useCalculationWorker } from "@/composables/scenarioCalculations/useCalculationWorker";
import { useLogger } from "@/composables/logger";
import FAToggleButton from "@/components/FAToggleButton.vue";
import type { WellProperties } from "@/composables/forms/formTypes";
import AddRemoveButtons from "@/components/AddRemoveButtons.vue";
import DataTable from "primevue/datatable";
import { useRoute, useRouter } from "vue-router";
import { computePressureFromDepthPsia } from "@/utils/computations";

import * as pdfMake from "pdfmake/build/pdfmake.min.js";
import { pdfFonts } from "@/plugins/vfsFonts";
import type { Content, TableCell } from "pdfmake/interfaces";
import { useSettingsStore } from "@/store";
import { DensityUnits } from "@/libs/units/density";

const liquidRate = "Liquid Rate";
const oilRate = "Oil Rate ";
const waterRate = "Water Rate ";
const thrustLoadBearingRatio = "Thrust Load Bearing Ratio";

const HPS_DISABLED_CONTROL_LABELS = [
  "Casing Pressure",
  "Intake Gas Separation Efficiency",
  "Natural Gas Separation Efficiency",
  "PBHP",
  "Producing Fluid Level",
  "SBHP",
  "Static Fluid Level",
  "Tubing Pressure",
  "Target Liquid Rate",
  "Motor Power Factor"
];

const HPS_DISABLED_WATCH_LABELS = [
  "Cable Efficiency",
  "Cable Voltage Drop",
  "Casing Pressure",
  "Flow Velocity by Motor",
  "Fluid Level Above Pump TVD",
  "MLE Operating Temperature",
  "Natural Gas Separation Efficiency",
  "Intake Gas Separation Efficiency",
  "Motor Operating Temperature",
  "PBHP",
  "Producing Fluid Level",
  "Required Discharge Pressure",
  "HPS Required Discharge Pressure",
  "Shut-In Pressure @ Wellhead",
  "SBHP",
  "Seal Efficiency",
  "Seal Pull Expansion Capacity Ratio",
  "Static Fluid Level",
  "System Efficiency",
  "Motor Power Factor",
  "Surface kVA Required",
  "Surface kW Required",
  "Surface Volts Required",
  "Total Cable Average Operating Temperature",
  "Total Cable Volts",
  "Tubing Pressure",
  "VSD Percent VFD Load (%)",
  "Well Flowing Pressure at Datum",
  "Oil Rate",
  "Water Rate"
];

const router = useRouter();
const route = useRoute();
const componentIsActive = computed(() => route.name === "ScenarioAnalysis");

const log = useLogger("Scenario Analysis");

const { queryCalculation, cancelCalculations } = useCalculationWorker();

const sizing = injectStrict(sizingKey, null);
const scenario = injectStrict(scenarioKey);

// redirect any hps scenarios for now
if (scenario.value?.pumping_type === Sizing_Pumping_Type_Enum.Hps) {
  router.push({
    name: "ScenarioInfo",
    params: {
      sizingId: route.params.sizingId,
      scenarioId: route.params.scenarioId
    }
  });
}

const destructuredScenario = useDestructureScenario(scenario);
const scenarioCalculations = useScenarioCalculations(destructuredScenario);

const { setScenarioAnalysisMode, scenarioAnalysisMode } = useSettingsStore();

setScenarioAnalysisMode("indiscrete");
onBeforeMount(() => {
  setScenarioAnalysisMode("indiscrete");
});

const isHps = computed(() => {
  return sizing?.value?.pumping_type === Sizing_Pumping_Type_Enum.Hps;
});

const useFullCrossProduct = computed(
  () => scenarioAnalysisMode.value === "indiscrete"
);

const maxControlsAllowed = computed(() =>
  scenarioAnalysisMode.value === "discrete" ? 8 : 4
);
const SCROLL_PAGE_SIZE = 10;
const { data: wellTestLiquidInputOptions } =
  useWellTestLiquidInputOptionsQuery();

const { data: wellTestGasInputOptions } = useWellTestGasInputOptionsQuery();

const focusedControlLabel = ref<string | null>(null);

const predictionFunctionOptions = reactive([
  {
    comment: "Calc Hz",
    value: predictPumpFrequency,
    disabled: computed(() =>
      selectedControls.value.some(
        ({ selectedControl }) =>
          selectedControl.defaultPredictionFn === predictDesiredFlowRate
      )
    )
  },
  {
    comment: "Calc Flow",
    value: predictDesiredFlowRate,
    disabled: computed(() =>
      selectedControls.value.some(
        ({ selectedControl }) =>
          selectedControl.defaultPredictionFn === predictPumpFrequency
      )
    )
  }
]);

const ITEM_SIZE = 50;

type AnalysisStep = Record<string, number | null>;

type PredictionFn = (
  scenario: UnwrapNestedRefs<DestructuredScenario>,
  neededCalcs?: { [key: string]: string }
) => Promise<{
  prediction: number;
  neededCalcs: AnalysisStep;
} | null>;

interface OfType {
  diff: string;
  isMainType: boolean;
}

type AnalysisVariable = {
  label: string;
  displayLabel?: string;
  unitType?: string;
  target?: ConvertableUnit;
  defaultPredictionFn?: PredictionFn;
  get: (calcs: ScenarioCalculations) => number;
  getPath: () => string;
  ofType?: OfType;
  control?: (
    scenario: UnwrapNestedRefs<DestructuredScenario>,
    val: number
  ) => UnwrapNestedRefs<DestructuredScenario>;
} & Pick<ConverterInputProps, "base" | "model" | "target">;

/**
 * This helper function allows us to declare variables at the same time
 * as defining a map of variables. The alternative is to explicitly define
 * a VariableKeys union type which seems excessive.
 */
function defineAnalysisVariables<T extends { [K: string]: AnalysisVariable }>(
  analysisVariables: T
) {
  return analysisVariables;
}
const overrideUnits = ref<{ [label: string]: ConvertableUnit }>({});
enum ShaftLoad {
  SEAL_HS = "Seal HS Shaft Load",
  SEAL_STANDARD = "Seal STD Shaft Load",
  PUMP_HS = "Pump HS Shaft Load",
  PUMP_STANDARD = "Pump STD Shaft Load",
  INTAKE_HS = "Intake HS Shaft Load",
  INTAKE_STANDARD = "Intake STD Shaft Load"
}
// need to add intakes in here too
enum TypeNamesForAnalysis {
  TYPEHSSEAL = "hsShaftLoadSeal",
  TYPESTANDARDSEAL = "standardShaftLoadSeal",
  TYPEHSPUMP = "hsShaftLoadPump",
  TYPESTANDARDPUMP = "standardShaftLoadPump",
  TYPESTANDARDINTAKE = "standardShaftLoadIntake",
  TYPEHSINTAKE = "hsShaftLoadIntake",
  FIRSTHSSEAL = "hsShaftLoadSeal0",
  FIRSTSTANDARDSEAL = "standardShaftLoadSeal0",
  FIRSTHSPUMP = "hsShaftLoadPump0",
  FIRSTSTANDARDPUMP = "standardShaftLoadPump0",
  FIRSTSTANDARDINTAKE = "standardShaftLoadIntake0",
  FIRSTHSINTAKE = "hsShaftLoadIntake0"
}

function createSealName(
  sealId: string,
  preFix: string,
  index: number,
  loadType: ShaftLoad
): string {
  const seal = destructuredScenario.seals.value[sealId];
  if (index === 0) {
    return loadType;
  }
  if (seal?.r_seal) {
    const sealName = `${seal.r_seal.outer_diameter_inches} / ${seal.r_seal.configuration} )`;
    return `${preFix} ${sealName}`;
  }
  return "";
}

function createPumpName(
  pumpId: string,
  preFix: string,
  index: number,
  loadType: ShaftLoad
): string {
  const pump = destructuredScenario.pumps.value[pumpId];
  if (index === 0) {
    return loadType;
  }
  if (pump?.r_pump) {
    return `${preFix} ${pump.r_pump.series_model})`;
  }
  return "";
}

function createIntakeName(
  intakeId: string,
  preFix: string,
  index: number,
  loadType: ShaftLoad
): string {
  const intake = destructuredScenario.intakes.value[intakeId];
  if (index === 0) {
    return loadType;
  }
  if (intake?.r_intake) {
    return `${preFix} ${intake.r_intake.intake_type})`;
  }
  return "";
}

function createIndividualPumpAndSealWatchVariables(): {
  [key: string]: AnalysisVariable;
} {
  const variables = Object.keys(destructuredScenario.seals.value).reduce(
    (acc, sealId, index) => {
      acc[`${TypeNamesForAnalysis.TYPEHSSEAL}${index + 1}`] = {
        label: createSealName(
          sealId,
          `Seal HS Shaft Load (`,
          index,
          ShaftLoad.SEAL_HS
        ),
        displayLabel: createSealName(
          sealId,
          `Seal HS Shaft Load (`,
          index + 1,
          ShaftLoad.SEAL_HS
        ),
        base: PercentageUnits.Ratio,
        target: PercentageUnits.Percentage,
        model: PercentageUnits.options(),
        ofType: {
          diff: `${TypeNamesForAnalysis.TYPEHSSEAL}${index}`,
          isMainType: index === 0
        },
        getPath: () => {
          return `sealCalculations.individual.value.${sealId}.highStrengthShaftRatingRatio`;
        },
        get: (calcs: ScenarioCalculations) => {
          const highStrengthRatio =
            calcs.sealCalculations.individual.value[sealId]
              ?.highStrengthShaftRatingRatio || 0;
          return highStrengthRatio;
        }
      };
      // Standard Shaft Load (Seal X)
      acc[`${TypeNamesForAnalysis.TYPESTANDARDSEAL}${index + 1}`] = {
        label: createSealName(
          sealId,
          `Seal STD Shaft Load (`,
          index,
          ShaftLoad.SEAL_STANDARD
        ),
        displayLabel: createSealName(
          sealId,
          `Seal STD Shaft Load (`,
          index + 1,
          ShaftLoad.SEAL_STANDARD
        ),
        base: PercentageUnits.Ratio,
        target: PercentageUnits.Percentage,
        model: PercentageUnits.options(),
        ofType: {
          diff: `${TypeNamesForAnalysis.TYPESTANDARDSEAL}${index}`,
          isMainType: index === 0
        },
        getPath: () => {
          return `sealCalculations.individual.value.${sealId}.standardShaftRatingRatio`;
        },
        get: (calcs: ScenarioCalculations) => {
          const standardShaftRatio =
            calcs.sealCalculations.individual.value[sealId]
              ?.standardShaftRatingRatio || 0;
          return standardShaftRatio;
        }
      };
      return acc;
    },
    {} as { [key: string]: AnalysisVariable }
  );

  // Adding HS Shaft Load (Pump X) and Standard Shaft Load (Pump X)
  Object.keys(destructuredScenario.pumps.value).forEach((pumpId, index) => {
    // HS Shaft Load (Pump X)
    variables[`${TypeNamesForAnalysis.TYPEHSPUMP}${index + 1}`] = {
      label: createPumpName(
        pumpId,
        `Pump HS Shaft Load (`,
        index,
        ShaftLoad.PUMP_HS
      ),
      displayLabel: createPumpName(
        pumpId,
        `Pump HS Shaft Load (`,
        index + 1,
        ShaftLoad.PUMP_HS
      ),
      base: PercentageUnits.Ratio,
      target: PercentageUnits.Percentage,
      model: PercentageUnits.options(),
      ofType: {
        diff: `${TypeNamesForAnalysis.TYPEHSPUMP}${index}`,
        isMainType: index === 0
      },
      getPath: () => {
        return `pumpCalculations.individual.value.${pumpId}.highStrengthShaftRatingRatio`;
      },
      get: (calcs: ScenarioCalculations) => {
        const highStrengthRatio =
          calcs.pumpCalculations.individual.value[pumpId]
            ?.highStrengthShaftRatingRatio || 0;
        return highStrengthRatio;
      }
    };
    // Standard Shaft Load (Pump X)
    variables[`${TypeNamesForAnalysis.TYPESTANDARDPUMP}${index + 1}`] = {
      label: createPumpName(
        pumpId,
        `Pump STD Shaft Load (`,
        index,
        ShaftLoad.PUMP_STANDARD
      ),
      displayLabel: createPumpName(
        pumpId,
        `Pump STD Shaft Load (`,
        index + 1,
        ShaftLoad.PUMP_STANDARD
      ),
      base: PercentageUnits.Ratio,
      target: PercentageUnits.Percentage,
      model: PercentageUnits.options(),
      ofType: {
        diff: `${TypeNamesForAnalysis.TYPESTANDARDPUMP}${index}`,
        isMainType: index === 0
      },
      getPath: () =>
        `pumpCalculations.individual.value.${pumpId}.standardShaftRatingRatio`,
      get: (calcs: ScenarioCalculations) => {
        const standardShaftRatio =
          calcs.pumpCalculations.individual.value[pumpId]
            ?.standardShaftRatingRatio || 0;
        return standardShaftRatio;
      }
    };
  });

  Object.keys(destructuredScenario.intakes.value).forEach((intakeId, index) => {
    // HS Shaft Load (Intake X)
    variables[`${TypeNamesForAnalysis.TYPEHSINTAKE}${index + 1}`] = {
      label: createIntakeName(
        intakeId,
        `Intake HS Shaft Load (`,
        index,
        ShaftLoad.INTAKE_HS
      ),
      displayLabel: createIntakeName(
        intakeId,
        `Intake HS Shaft Load (`,
        index + 1,
        ShaftLoad.INTAKE_HS
      ),
      base: PercentageUnits.Ratio,
      target: PercentageUnits.Percentage,
      model: PercentageUnits.options(),
      ofType: {
        diff: `${TypeNamesForAnalysis.TYPEHSINTAKE}${index}`,
        isMainType: index === 0
      },
      getPath: () =>
        `intakeCalculations.individual.value.${intakeId}.highStrengthShaftRatingRatio`,
      get: (calcs: ScenarioCalculations) => {
        const highStrengthRatio =
          calcs.intakeCalculations.individual.value[intakeId]
            ?.highStrengthShaftRatingRatio || 0;
        return highStrengthRatio;
      }
    };

    // Standard Shaft Load (Intake X)
    variables[`${TypeNamesForAnalysis.TYPESTANDARDINTAKE}${index + 1}`] = {
      label: createIntakeName(
        intakeId,
        `Intake STD Shaft Load (`,
        index,
        ShaftLoad.INTAKE_STANDARD
      ),
      displayLabel: createIntakeName(
        intakeId,
        `Intake STD Shaft Load (`,
        index + 1,
        ShaftLoad.INTAKE_STANDARD
      ),
      base: PercentageUnits.Ratio,
      target: PercentageUnits.Percentage,
      model: PercentageUnits.options(),
      ofType: {
        diff: `${TypeNamesForAnalysis.TYPESTANDARDINTAKE}${index}`,
        isMainType: index === 0
      },
      getPath: () =>
        `intakeCalculations.individual.value.${intakeId}.standardShaftRatingRatio`,
      get: (calcs: ScenarioCalculations) => {
        const standardShaftRatio =
          calcs.intakeCalculations.individual.value[intakeId]
            ?.standardShaftRatingRatio || 0;
        return standardShaftRatio;
      }
    };
  });
  return variables;
}

function makeAnalysisVariables() {
  const analysisVariables = defineAnalysisVariables(
    (() => {
      const scenarioUnits = useScenarioUnits(scenario, sizing);
      return {
        ...createIndividualPumpAndSealWatchVariables(),
        fluidSpecificGravity: {
          label: "Fluid SG @ Intake",
          base: DensityUnits.SpecificGravity,
          model: DensityUnits.options(),
          unitType: "DensityUnits",
          target: overrideUnits.value.DensityUnits,
          get(calcs) {
            return calcs.fluidProperties.fluidPropertiesAtMotor.value
              .fluidSpecificGravity;
          },
          getPath: () =>
            "fluidProperties.fluidPropertiesAtMotor.value.fluidSpecificGravity"
        },
        totalCablesVoltDropV: {
          label: "Cable Voltage Drop",
          base: VoltageUnits.Volts,
          model: VoltageUnits.options(),
          unitType: "VoltageUnits",
          target: overrideUnits.value.VoltageUnits,
          get(calcs) {
            return calcs.cableCalculations.aggregate.totalCablesVoltLossV.value;
          },
          getPath: () =>
            "cableCalculations.aggregate.totalCablesVoltLossV.value"
        },
        casingPressurePsia: {
          label: "Casing Pressure",
          base: PressureUnits.PoundsPerSquareInchAbsolute,
          model: PressureUnits.options(),
          unitType: "PressureUnits",
          target:
            overrideUnits.value.PressureUnits ||
            scenarioUnits.getPressureUnit("Casing Pressure.write.PSIA").value,
          get(calcs) {
            return calcs.basicData.value.bottomCasingPressurePSIA;
          },
          getPath: () => "basicData.value.bottomCasingPressurePSIA",
          control(scenario, val) {
            return reactive({
              ...scenario,
              parameters: {
                ...scenario.parameters,
                __typename: "scenario_parameters",
                bottom_casing_pressure_psia: val
              }
            });
          }
        },
        flowVelocityAroundMotorFTSEC: {
          label: "Flow Velocity by Motor",
          base: SpeedUnits.FeetPerSecond,
          model: SpeedUnits.options(),
          unitType: "SpeedUnits",
          target: overrideUnits.value.SpeedUnits,
          get(calcs) {
            return calcs.motorCalculations.flowVelocityAroundMotorFTSEC.value;
          },
          getPath: () => "motorCalculations.flowVelocityAroundMotorFTSEC.value"
        },
        intakeFlowRateSTBD: {
          label: "Flowrate at Intake",
          base: LiquidFlowRateUnits.BarrelsPerDay,
          model: LiquidFlowRateUnits.options(),
          unitType: "LiquidFlowRateUnits",
          target: overrideUnits.value.LiquidFlowRateUnits,
          get(calcs) {
            return calcs.pumpCalculations.aggregate.intakeFlowRateSTBD.value;
          },
          getPath: () => "pumpCalculations.aggregate.intakeFlowRateSTBD.value"
        },
        dischargeFlowRateSTBD: {
          label: "Flowrate at Discharge",
          base: LiquidFlowRateUnits.BarrelsPerDay,
          model: LiquidFlowRateUnits.options(),
          unitType: "LiquidFlowRateUnits",
          target: overrideUnits.value.LiquidFlowRateUnits,
          get(calcs) {
            return calcs.pumpCalculations.aggregate.dischargeFlowRateSTBD.value;
          },
          getPath: () =>
            "pumpCalculations.aggregate.dischargeFlowRateSTBD.value"
        },
        liquidAbovePumpTvdFt: {
          label: "Fluid Level Above Pump TVD",
          base: LargeLengthUnits.Feet,
          model: LargeLengthUnits.options(),
          unitType: "LargeLengthUnits",
          target: overrideUnits.value.LargeLengthUnits,
          get(calcs) {
            return calcs.intakeCalculations.aggregate.liquidAbovePumpTvdFt
              .value;
          },
          getPath: () =>
            "intakeCalculations.aggregate.liquidAbovePumpTvdFt.value"
        },
        gasEnteringPumpRatio: {
          label: "Free Gas % Entering Pump",
          base: PercentageUnits.Ratio,
          model: PercentageUnits.options(),
          unitType: "PercentageUnits",
          target: PercentageUnits.Percentage,
          get(calcs) {
            return calcs.fluidProperties
              .fluidPropertiesAfterAllGasSeparationAtPump.value
              .gasFlowVolumeRatio;
          },
          getPath: () =>
            "fluidProperties.fluidPropertiesAfterAllGasSeparationAtPump.value.gasFlowVolumeRatio"
        },
        pumpHz: {
          unitType: "FrequencyUnits",
          label: "Frequency",
          base: FrequencyUnits.Hertz,
          model: FrequencyUnits.options(),
          defaultPredictionFn: predictDesiredFlowRate,
          get(calcs) {
            return calcs.adjustedPumpHzFrequency.value;
          },
          getPath: () => "adjustedPumpHzFrequency.value",
          control(scenario, val) {
            // Adjust pumphz here as we need to run the calcs with the
            // 'real' hz value instead of the value the user is inputting
            // in case they have a 4 pole motor (i.e they provide 100hz value
            // but the calcs really need to run it with 50hz)
            const motorPoles = scenario.equipment?.r_motor?.poles || 2;
            const pumpHz = val / (motorPoles / 2);
            return reactive({
              ...scenario,
              parameters: {
                ...scenario.parameters,
                __typename: "scenario_parameters",
                pump_hz: pumpHz
              }
            });
          }
        },
        gasHeadReratePercentage: {
          label: "Gas Head Rerate",
          base: PercentageUnits.Ratio,
          model: PercentageUnits.options(),
          target: PercentageUnits.Percentage,
          unitType: "PercentageUnits",
          get(calcs) {
            return calcs.pumpCalculations.aggregate.gasHeadRerateRatio.value;
          },
          getPath: () => "pumpCalculations.aggregate.gasHeadRerateRatio.value"
        },
        gasLiquidRatioScfStb: {
          label: "Gas Liquid Ratio",
          base: LiquidRatioUnits.StandardCubicFootPerStockTankBarrel,
          model: LiquidRatioUnits.options(),
          unitType: "LiquidRatioUnits",
          target:
            overrideUnits.value.LiquidRatioUnits ||
            scenarioUnits.getLiquidRatioUnit("Gas/Liquid ratio.write.SCF/STB")
              .value,
          get(calcs) {
            return calcs.wellTestFlowRates.value.gasLiquidRatioSCFSTB;
          },
          getPath: () => "wellTestFlowRates.value.gasLiquidRatioSCFSTB",
          control(scenario, val) {
            return reactive({
              ...scenario,
              wellProperties: {
                ...scenario.wellProperties,
                __typename: "scenario_well_properties",
                ...extrapolateFlowRates(
                  wellPropertiesWithAdjustedInputTypes(scenario),
                  "gasLiquidRatioScfStb",
                  val
                )
              }
            });
          }
        },
        gasOilRatioScfStb: {
          label: "Gas Oil Ratio",
          base: LiquidRatioUnits.StandardCubicFootPerStockTankBarrel,
          model: LiquidRatioUnits.options(),
          unitType: "LiquidRatioUnits",
          target:
            overrideUnits.value.LiquidRatioUnits ||
            scenarioUnits.getLiquidRatioUnit("Gas/Oil ratio.write.SCF/STB")
              .value,
          get(calcs) {
            if (settings.predictionFunction === predictDesiredFlowRate) {
              return calcs.surfaceFlowRates.value.gasOilRatioSCFSTB;
            } else {
              return calcs.wellTestFlowRates.value.gasOilRatioSCFSTB;
            }
          },
          getPath() {
            if (settings.predictionFunction === predictDesiredFlowRate) {
              return "surfaceFlowRates.value.gasOilRatioSCFSTB";
            } else {
              return "wellTestFlowRates.value.gasOilRatioSCFSTB";
            }
          },
          control(scenario, val) {
            return reactive({
              ...scenario,
              wellProperties: {
                ...scenario.wellProperties,
                __typename: "scenario_well_properties",
                ...extrapolateFlowRates(
                  wellPropertiesWithAdjustedInputTypes(scenario),
                  "gasOilRatioScfStb",
                  val
                )
              }
            });
          }
        },
        gasRateScfD: {
          label: "Gas Rate",
          base: GasFlowRateUnits.CubicFeetPerDay,
          model: GasFlowRateUnits.options(),
          unitType: "GasFlowRateUnits",
          target:
            overrideUnits.value.GasFlowRateUnits ||
            scenarioUnits.getGasFlowUnit("Gas Rate.write.mscf/d").value,
          get(calcs) {
            if (settings.predictionFunction === predictDesiredFlowRate) {
              return calcs.surfaceFlowRates.value.gasFlowRateSCFD;
            } else {
              return calcs.wellTestFlowRates.value.gasFlowRateSCFD;
            }
          },
          getPath() {
            if (settings.predictionFunction === predictDesiredFlowRate) {
              return "surfaceFlowRates.value.gasFlowRateSCFD";
            } else {
              return "wellTestFlowRates.value.gasFlowRateSCFD";
            }
          },
          control(scenario, val) {
            return reactive({
              ...scenario,
              wellProperties: {
                ...scenario.wellProperties,
                __typename: "scenario_well_properties",
                ...extrapolateFlowRates(
                  wellPropertiesWithAdjustedInputTypes(scenario),
                  "gasFlowRateScfD",
                  val
                )
              }
            });
          }
        },
        naturalGasSeparationEfficiencyPercentage: {
          label: "Natural Gas Separation Efficiency",
          base: PercentageUnits.Percentage,
          model: [PercentageUnits.Percentage],
          unitType: "PercentageUnits",
          target: overrideUnits.value.PercentageUnits,
          get(calcs) {
            return convert(
              calcs.intakeCalculations.aggregate
                .naturalGasSeparationEfficiencyRatio.value,
              PercentageUnits.Ratio,
              PercentageUnits.Percentage
            );
          },
          getPath() {
            return "intakeCalculations.aggregate.naturalGasSeparationEfficiencyRatio.value";
          },
          control(scenario, val) {
            return reactive({
              ...scenario,
              parameters: {
                ...scenario.parameters,
                __typename: "scenario_parameters",
                natural_override_gas_separation_percent: val,
                natural_gas_separation_override_type:
                  Override_Options_Enum.Override
              }
            });
          }
        },
        intakeGasSeparationEfficiencyPercentage: {
          label: "Intake Gas Separation Efficiency",
          base: PercentageUnits.Percentage,
          model: [PercentageUnits.Percentage],
          unitType: "PercentageUnits",
          target: overrideUnits.value.PercentageUnits,
          get(calcs) {
            return convert(
              calcs.intakeCalculations.aggregate
                .intakeGasSeparationEfficiencyRatio.value,
              PercentageUnits.Ratio,
              PercentageUnits.Percentage
            );
          },
          getPath() {
            return "intakeCalculations.aggregate.intakeGasSeparationEfficiencyRatio.value";
          },
          control(scenario, val) {
            return reactive({
              ...scenario,
              parameters: {
                ...scenario.parameters,
                __typename: "scenario_parameters",
                intake_override_gas_separation_percent: val,
                intake_gas_separation_override_type:
                  Override_Options_Enum.Override
              }
            });
          }
        },
        wellTestLiquidRateStbD: {
          unitType: "LiquidFlowRateUnits",
          label: "Input Liquid Rate",
          base: LiquidFlowRateUnits.BarrelsPerDay,
          model: LiquidFlowRateUnits.options(),
          target:
            overrideUnits.value.LiquidFlowRateUnits ||
            scenarioUnits.getLiquidFlowUnit("Total Liquid Rate.write.STB/D")
              .value,
          get(calcs) {
            return calcs.wellTestFlowRates.value.liquidFlowRateSTBD;
          },
          getPath() {
            return "wellTestFlowRates.value.liquidFlowRateSTBD";
          },
          control(scenario, val) {
            return reactive({
              ...scenario,
              wellProperties: {
                ...scenario.wellProperties,
                __typename: "scenario_well_properties",
                ...extrapolateFlowRates(
                  wellPropertiesWithAdjustedInputTypes(scenario),
                  "totalLiquidFlowRateStbD",
                  val
                )
              }
            });
          }
        },
        wellTestOilRateStbD: {
          label: "Input Oil Rate",
          unitType: "LiquidFlowRateUnits",
          base: LiquidFlowRateUnits.BarrelsPerDay,
          model: LiquidFlowRateUnits.options(),
          target:
            overrideUnits.value.LiquidFlowRateUnits ||
            scenarioUnits.getLiquidFlowUnit("Oil Rate.write.STB/D").value,
          get(calcs) {
            return calcs.wellTestFlowRates.value.oilFlowRateSTBD;
          },
          getPath() {
            return "wellTestFlowRates.value.oilFlowRateSTBD";
          },
          control(scenario, val) {
            return reactive({
              ...scenario,
              wellProperties: {
                ...scenario.wellProperties,
                __typename: "scenario_well_properties",
                ...extrapolateFlowRates(
                  wellPropertiesWithAdjustedInputTypes(scenario),
                  "oilFlowRateStbD",
                  val
                )
              }
            });
          }
        },
        wellTestWaterRateStbD: {
          label: "Input Water Rate",
          unitType: "LiquidFlowRateUnits",
          base: LiquidFlowRateUnits.BarrelsPerDay,
          model: LiquidFlowRateUnits.options(),
          target:
            overrideUnits.value.LiquidFlowRateUnits ||
            scenarioUnits.getLiquidFlowUnit("Water Rate.write.STB/D").value,
          get(calcs) {
            return calcs.wellTestFlowRates.value.waterFlowRateSTBD;
          },
          getPath() {
            return "wellTestFlowRates.value.waterFlowRateSTBD";
          },
          control(scenario, val) {
            return reactive({
              ...scenario,
              wellProperties: {
                ...scenario.wellProperties,
                __typename: "scenario_well_properties",
                ...extrapolateFlowRates(
                  wellPropertiesWithAdjustedInputTypes(scenario),
                  "waterFlowRateStbD",
                  val
                )
              }
            });
          }
        },
        cableMaxTemperatureF: {
          label: "MLE Operating Temperature",
          base: TemperatureUnits.Fahrenheit,
          model: TemperatureUnits.options(),
          unitType: "TemperatureUnits",
          target:
            overrideUnits.value.TemperatureUnits ||
            scenarioUnits.getTempUnit("Cable Temperature.convert.F°").value,
          get(calcs) {
            return calcs.motorLeadExtensionCalculations.mleCableData.value
              .cableMaxTemperatureF;
          },
          getPath: () =>
            "motorLeadExtensionCalculations.mleCableData.value.cableMaxTemperatureF"
        },
        motorEfficiencyRatio: {
          label: "Motor Efficiency",
          base: PercentageUnits.Ratio,
          model: PercentageUnits.options(),
          target: PercentageUnits.Percentage,
          unitType: "PercentageUnits",
          get(calcs) {
            return calcs.motorCalculations.motorEfficiencyRatio.value;
          },
          getPath: () => "motorCalculations.motorEfficiencyRatio.value"
        },
        motorAmps: {
          label: "Motor Amps",
          unitType: "CurrentUnits",
          base: CurrentUnits.Amperes,
          model: CurrentUnits.options(),
          target:
            overrideUnits.value.CurrentUnits ||
            scenarioUnits.getCurrentUnit("Motor Amps").value,
          get(calcs) {
            return calcs.motorCalculations.motorCurrentA.value;
          },
          getPath: () => "motorCalculations.motorCurrentA.value"
        },
        totalRequiredBrakeHP: {
          label: "Motor HP Required",
          base: PowerUnits.HorsePower,
          model: [PowerUnits.HorsePower],
          unitType: "PowerUnits",
          target:
            overrideUnits.value.PowerUnits ||
            scenarioUnits.getHydraulicPowerUnit().value,
          get(calcs) {
            return calcs.motorCalculations.totalRequiredBrakeHP.value;
          },
          getPath: () => "motorCalculations.totalRequiredBrakeHP.value"
        },
        motorLoadPercentage: {
          label: "Motor Load",
          base: PercentageUnits.Ratio,
          model: PercentageUnits.options(),
          target: PercentageUnits.Percentage,
          unitType: "PercentageUnits",
          get(calcs) {
            return calcs.motorCalculations.motorLoadRatio.value;
          },
          getPath: () => "motorCalculations.motorLoadRatio.value"
        },
        motorCurrentA: {
          label: "Motor Operating Amps",
          base: CurrentUnits.Amperes,
          model: CurrentUnits.options(),
          unitType: "CurrentUnits",
          target: overrideUnits.value.CurrentUnits,
          get(calcs) {
            return calcs.motorCalculations.motorCurrentA.value;
          },
          getPath: () => "motorCalculations.motorCurrentA.value"
        },
        motorInternalTemperatureF: {
          label: "Motor Operating Temperature",
          base: TemperatureUnits.Fahrenheit,
          model: TemperatureUnits.options(),
          unitType: "TemperatureUnits",
          target: overrideUnits.value.TemperatureUnits,
          get(calcs) {
            return calcs.motorCalculations.motorInternalTemperatureF.value;
          },
          getPath: () => "motorCalculations.motorInternalTemperatureF.value"
        },
        motorVoltageV: {
          label: "Motor Operating Volts",
          base: VoltageUnits.Volts,
          model: VoltageUnits.options(),
          unitType: "VoltageUnits",
          target: overrideUnits.value.VoltageUnits,
          get(calcs) {
            return calcs.motorCalculations.motorVoltageV.value;
          },
          getPath: () => "motorCalculations.motorVoltageV.value"
        },
        motorPowerFactor: {
          label: "Motor Power Factor",
          base: PercentageUnits.Ratio,
          model: PercentageUnits.options(),
          target: PercentageUnits.Percentage,
          unitType: "PercentageUnits",
          get(calcs) {
            return calcs.motorCalculations.motorPowerFactor.value;
          },
          getPath: () => "motorCalculations.motorPowerFactor.value"
        },
        operatingRPM: {
          label: "Motor RPM",
          base: GenericUnits.RevolutionsPerMinute,
          model: [GenericUnits.RevolutionsPerMinute],
          unitType: "GenericUnits",
          get(calcs) {
            return calcs.pumpCalculations.aggregate.operatingRPM.value;
          },
          getPath: () => "pumpCalculations.aggregate.operatingRPM.value"
        },
        motorLoadRatio: {
          label: "Motor Shaft Load %",
          base: PercentageUnits.Ratio,
          model: PercentageUnits.options(),
          target: PercentageUnits.Percentage,
          unitType: "PercentageUnits",
          get(calcs) {
            return calcs.motorCalculations.motorLoadRatio.value;
          },
          getPath: () => "motorCalculations.motorLoadRatio.value"
        },
        oilFlowRateSTBD: {
          label: "Oil Rate",
          base: LiquidFlowRateUnits.BarrelsPerDay,
          model: LiquidFlowRateUnits.options(),
          unitType: "LiquidFlowRateUnits",
          target:
            overrideUnits.value.LiquidFlowRateUnits ||
            scenarioUnits.getLiquidFlowUnit("Oil Rate.convert.STB/D").value,
          get(calcs) {
            if (settings.predictionFunction === predictDesiredFlowRate) {
              return calcs.surfaceFlowRates.value.oilFlowRateSTBD;
            } else {
              return calcs.wellTestFlowRates.value.oilFlowRateSTBD;
            }
          },
          getPath() {
            if (settings.predictionFunction === predictDesiredFlowRate) {
              return "surfaceFlowRates.value.oilFlowRateSTBD";
            } else {
              return "wellTestFlowRates.value.oilFlowRateSTBD";
            }
          }
        },
        totalRequired60HzBrakeHP: {
          label: "Operating HP Required @ 60Hz",
          base: PowerUnits.HorsePower,
          model: [PowerUnits.HorsePower],
          unitType: "PowerUnits",
          target:
            overrideUnits.value.PowerUnits ||
            scenarioUnits.getHydraulicPowerUnit().value,
          get(calcs) {
            return calcs.motorCalculations.totalRequired60HzBrakeHP.value;
          },
          getPath: () => "motorCalculations.totalRequired60HzBrakeHP.value"
        },
        producingBottomHolePressurePsia: {
          label: "PBHP",
          base: PressureUnits.PoundsPerSquareInchAbsolute,
          model: PressureUnits.options(),
          unitType: "PressureUnits",
          target:
            overrideUnits.value.PressureUnits ||
            scenarioUnits.getPressureUnit(
              "Producing Bottomhole Pressure at Datum [Pwf].write.PSIA"
            ).value,
          get(calcs) {
            return calcs.basicData.value.producingBottomholePressurePSIA;
          },
          getPath: () => "basicData.value.producingBottomholePressurePSIA",
          control(scenario, val) {
            return reactive({
              ...scenario,
              wellProperties: {
                ...scenario.wellProperties,
                __typename: "scenario_well_properties",
                well_test_producing_bottom_hole_pressure_psia: val
              }
            });
          }
        },
        productivityIndexStbDPsi: {
          label: "PI",
          unitType: "ProductivityIndexUnits",
          base: ProductivityIndexUnits.BarrelDayPsi,
          model: ProductivityIndexUnits.options(),
          target:
            overrideUnits.value.ProductivityIndexUnits ||
            scenarioUnits.getProductivityFlowUnit(
              "Productivity Index.write.stb/d/psi"
            ).value,
          get(calcs) {
            return calcs.productivityIndexStbdPsia.value;
          },
          getPath: () => "productivityIndexStbdPsia.value",
          control(scenario, val) {
            return reactive({
              ...scenario,
              wellProperties: {
                ...scenario.wellProperties,
                __typename: "scenario_well_properties",
                ...extrapolateFlowRates(
                  wellPropertiesWithAdjustedInputTypes(scenario),
                  "totalLiquidFlowRateStbD",
                  val
                )
              }
            });
          }
        },
        producedTotalDynamicHeadFt: {
          unitType: "LargeLengthUnits",
          label: "Produced TDH",
          base: LargeLengthUnits.Feet,
          model: PressureUnits.options(),
          target:
            overrideUnits.value.LargeLengthUnits ||
            scenarioUnits.getLargeLengthUnit("Produced TDH.convert.Feet").value,
          get(calcs) {
            return calcs.pumpCalculations.aggregate.producedTotalDynamicHeadFt
              .value;
          },
          getPath: () =>
            "pumpCalculations.aggregate.producedTotalDynamicHeadFt.value"
        },
        producingFluidLevelFt: {
          label: "Producing Fluid Level",
          base: LargeLengthUnits.Feet,
          model: LargeLengthUnits.options(),
          unitType: "LargeLengthUnits",
          target:
            overrideUnits.value.LargeLengthUnits ||
            scenarioUnits.getLargeLengthUnit(
              "Fluid Above Pump (MD).convert.Feet"
            ).value,
          get(calcs) {
            return calcs.basicData.value.producingFluidLevelFeet;
          },
          getPath: () => "basicData.value.producingFluidLevelFeet",
          control(scenario, val) {
            return reactive({
              ...scenario,
              wellProperties: {
                ...scenario.wellProperties,
                __typename: "scenario_well_properties",
                well_test_producing_bottom_hole_pressure_psia:
                  computePressureFromDepthPsia(
                    scenarioCalculations.equipmentLengths.datumPointFt.value,
                    val,
                    scenarioCalculations.liquidDensityAtDatumLbmft3.value
                  )
              }
            });
          }
        },
        pumpDischargePressure: {
          label: "Pump Discharge Pressure",
          unitType: "PressureUnits",
          base: PressureUnits.PoundsPerSquareInchGauge,
          model: PressureUnits.options(),
          target:
            overrideUnits.value.PressureUnits ||
            scenarioUnits.getPressureUnit("Pump Discharge Pressure").value,
          get(calcs) {
            return calcs.pumpCalculations.aggregate
              .producedDischargePressurePSIA.value;
          },
          getPath: () =>
            "pumpCalculations.aggregate.producedDischargePressurePSIA.value"
        },

        requiredPumpBrakeHP: {
          label: "Pump HP Required",
          unitType: "PowerUnits",
          base: PowerUnits.HorsePower,
          model: [PowerUnits.HorsePower],
          target:
            overrideUnits.value.PowerUnits ||
            scenarioUnits.getHydraulicPowerUnit().value,
          get(calcs) {
            return calcs.pumpCalculations.aggregate.requiredPumpBrakeHP.value;
          },
          getPath: () => "pumpCalculations.aggregate.requiredPumpBrakeHP.value"
        },
        pumpIntakePressurePsia: {
          unitType: "PressureUnits",
          label: "Pump Intake Pressure",
          base: PressureUnits.PoundsPerSquareInchAbsolute,
          model: PressureUnits.options(),
          target:
            overrideUnits.value.PressureUnits ||
            scenarioUnits.getPressureUnit("Pump Intake Pressure.convert.PSIA")
              .value,
          get(calcs) {
            return calcs.pumpIntakePressurePSIA.value;
          },
          getPath: () => "pumpIntakePressurePSIA.value"
        },
        pumpIntakeTemperatureF: {
          label: "Pump Intake Temperature",
          base: TemperatureUnits.Fahrenheit,
          model: TemperatureUnits.options(),
          unitType: "TemperatureUnits",
          target: overrideUnits.value.TemperatureUnits,
          get(calcs) {
            return calcs.pumpIntakeTemperatureF.value;
          },
          getPath: () => "pumpIntakeTemperatureF.value"
        },
        shutInPressurePSIA: {
          label: "Shut-In Pressure @ Discharge",
          base: PressureUnits.PoundsPerSquareInchAbsolute,
          model: PressureUnits.options(),
          unitType: "PressureUnits",
          target:
            overrideUnits.value.PressureUnits ||
            scenarioUnits.getPressureUnit(
              "Shut-In Pressure @ Discharge.convert.PSIA"
            ).value,
          get(calcs) {
            return calcs.pumpCalculations.aggregate
              .dischargeShutInBurstPressurePSIA.value;
          },
          getPath: () =>
            "pumpCalculations.aggregate.dischargeShutInBurstPressurePSIA.value"
        },
        wellheadShutInBurstPressurePSIA: {
          unitType: "PressureUnits",
          label: "Shut-In Pressure @ Wellhead",
          base: PressureUnits.PoundsPerSquareInchAbsolute,
          model: PressureUnits.options(),
          target: overrideUnits.value.PressureUnits,
          get(calcs) {
            return calcs.pumpCalculations.aggregate
              .wellheadShutInBurstPressurePSIA.value;
          },
          getPath: () =>
            "pumpCalculations.aggregate.wellheadShutInBurstPressurePSIA.value"
        },
        requiredDischargePressurePSIA: {
          unitType: "PressureUnits",
          label: "Required Discharge Pressure",
          base: PressureUnits.PoundsPerSquareInchAbsolute,
          model: PressureUnits.options(),
          target: overrideUnits.value.PressureUnits,
          get(calcs) {
            return calcs.pumpCalculations.aggregate
              .requiredDischargePressurePSIA.value;
          },
          getPath: () =>
            "pumpCalculations.aggregate.requiredDischargePressurePSIA.value"
        },
        requiredTotalDynamicHeadFt: {
          label: "Required TDH",
          base: LargeLengthUnits.Feet,
          model: LargeLengthUnits.options(),
          unitType: "LargeLengthUnits",
          target:
            overrideUnits.value.LargeLengthUnits ||
            scenarioUnits.getLargeLengthUnit("Required TDH.convert.Feet").value,
          get(calcs) {
            return calcs.pumpCalculations.aggregate.requiredTotalDynamicHeadFt
              .value;
          },
          getPath: () =>
            "pumpCalculations.aggregate.requiredTotalDynamicHeadFt.value"
        },
        staticBottomholePressurePsia: {
          label: "SBHP",
          base: PressureUnits.PoundsPerSquareInchAbsolute,
          model: PressureUnits.options(),
          unitType: "PressureUnits",
          target:
            overrideUnits.value.PressureUnits ||
            scenarioUnits.getPressureUnit(
              "Static Bottomhole Pressure at Datum [Pr].write.PSIA"
            ).value,
          get(calcs) {
            return calcs.basicData.value.staticBottomholePressurePSIA;
          },
          getPath: () => "basicData.value.staticBottomholePressurePSIA",
          control(scenario, val) {
            // Calculate the original pressure drawdown
            const originalPressureDrawdown =
              scenario.wellProperties
                ?.well_test_static_bottom_hole_pressure_psia -
              scenario.wellProperties
                ?.well_test_producing_bottom_hole_pressure_psia;
            // Adjust PBHP to maintain the same pressure drawdown
            const adjustedPBHP = val - originalPressureDrawdown;
            return reactive({
              ...scenario,
              wellProperties: {
                ...scenario.wellProperties,
                __typename: "scenario_well_properties",
                well_test_static_bottom_hole_pressure_psia: val,
                well_test_producing_bottom_hole_pressure_psia: adjustedPBHP
              }
            });
          }
        },
        sealBearingLoadRatio: {
          label: "Seal Bearing Load Ratio",
          base: PercentageUnits.Ratio,
          model: PercentageUnits.options(),
          target: PercentageUnits.Percentage,
          unitType: "PercentageUnits",
          get(calcs) {
            return calcs.sealCalculations.aggregate.sealBearingLoadRatio.value;
          },
          getPath: () => "sealCalculations.aggregate.sealBearingLoadRatio.value"
        },
        sealExpansionCapacityRatio: {
          label: "Seal Pull Expansion Capacity Ratio",
          base: PercentageUnits.Ratio,
          model: PercentageUnits.options(),
          target: PercentageUnits.Percentage,
          unitType: "PercentageUnits",
          get(calcs) {
            return calcs.sealCalculations.aggregate
              .sealUpperChamberPullExpansionRatio.value;
          },
          getPath: () =>
            "sealCalculations.aggregate.sealExpansionCapacityRatio.value"
        },
        staticFluidLevelFt: {
          label: "Static Fluid Level",
          base: LargeLengthUnits.Feet,
          model: LargeLengthUnits.options(),
          unitType: "LargeLengthUnits",
          target:
            overrideUnits.value.LargeLengthUnits ||
            scenarioUnits.getLargeLengthUnit("Fluid Level (MD).convert.Feet")
              .value,
          get(calcs) {
            return calcs.basicData.value.staticFluidLevelFeet;
          },
          getPath: () => "basicData.value.staticFluidLevelFeet",
          control(scenario, val) {
            return reactive({
              ...scenario,
              wellProperties: {
                ...scenario.wellProperties,
                __typename: "scenario_well_properties",
                well_test_static_bottom_hole_pressure_psia:
                  computePressureFromDepthPsia(
                    scenarioCalculations.equipmentLengths.datumPointFt.value,
                    val,
                    scenarioCalculations.liquidDensityAtDatumLbmft3.value
                  )
              }
            });
          }
        },
        requiredKVA: {
          label: "Surface kVA Required",
          base: ApparentPowerUnits.KiloVoltAmps,
          model: ApparentPowerUnits.options(),
          unitType: "ApparentPowerUnits",
          target:
            overrideUnits.value.ApparentPowerUnits ||
            scenarioUnits.getApparentPowerUnit("Surface kVA.convert.kVA").value,
          get(calcs) {
            return calcs.surfaceDriveCalculations.requiredKVA.value;
          },
          getPath: () => "surfaceDriveCalculations.requiredKVA.value"
        },
        systemPowerKW: {
          label: "Surface kW Required",
          base: PowerUnits.KiloWatts,
          model: PowerUnits.options(),
          unitType: "PowerUnits",
          target:
            overrideUnits.value.PowerUnits ||
            scenarioUnits.getRealPowerUnit("System Power.convert.kW").value,
          get(calcs) {
            return calcs.surfaceDriveCalculations.systemPowerKW.value;
          },
          getPath: () => "surfaceDriveCalculations.systemPowerKW.value"
        },
        requiredVoltageV: {
          label: "Surface Volts Required",
          base: VoltageUnits.Volts,
          model: VoltageUnits.options(),
          unitType: "VoltageUnits",
          target:
            overrideUnits.value.VoltageUnits ||
            scenarioUnits.getVoltageUnit("Surface Voltage.convert.V").value,
          get(calcs) {
            return calcs.surfaceDriveCalculations.requiredVoltageV.value;
          },
          getPath: () => "surfaceDriveCalculations.requiredVoltageV.value"
        },
        targetLiquidRateStbD: {
          unitType: "LiquidFlowRateUnits",
          label: "Target Liquid Rate",
          base: LiquidFlowRateUnits.BarrelsPerDay,
          model: LiquidFlowRateUnits.options(),
          target:
            overrideUnits.value.LiquidFlowRateUnits ||
            scenarioUnits.getLiquidFlowUnit("Target Liquid Rate.write.STB/D")
              .value,
          defaultPredictionFn: predictPumpFrequency,
          get(calcs) {
            return calcs.surfaceFlowRates.value.liquidFlowRateSTBD;
          },
          getPath: () => "surfaceFlowRates.value.liquidFlowRateSTBD",
          control(scenario, val) {
            return reactive({
              ...scenario,
              parameters: {
                ...scenario.parameters,
                __typename: "scenario_parameters",
                target_liquid_rate_std_barrel_day: val
              }
            });
          }
        },
        totalCablesAverageTemperatureF: {
          label: "Total Cable Average Operating Temperature",
          base: TemperatureUnits.Fahrenheit,
          model: TemperatureUnits.options(),
          unitType: "TemperatureUnits",
          target:
            overrideUnits.value.TemperatureUnits ||
            scenarioUnits.getTempUnit("Cable Temperature.convert.F°").value,
          get(calcs) {
            return calcs.cableCalculations.aggregate
              .totalCablesAverageTemperatureF.value;
          },
          getPath: () =>
            "cableCalculations.aggregate.totalCablesAverageTemperatureF.value"
        },
        totalCablesVoltLossV: {
          label: "Total Cable Volts",
          base: VoltageUnits.Volts,
          model: VoltageUnits.options(),
          target:
            overrideUnits.value.VoltageUnits ||
            scenarioUnits.getVoltageUnit("Total Cables Voltage Loss.convert.V")
              .value,
          unitType: "VoltageUnits",
          get(calcs) {
            return calcs.cableCalculations.aggregate.totalCablesVoltLossV.value;
          },
          getPath: () =>
            "cableCalculations.aggregate.totalCablesVoltLossV.value"
        },
        tubingPressurePsia: {
          label: "Tubing Pressure",
          base: PressureUnits.PoundsPerSquareInchAbsolute,
          model: PressureUnits.options(),
          unitType: "PressureUnits",
          target:
            overrideUnits.value.PressureUnits ||
            scenarioUnits.getPressureUnit("Tubing Pressure.write.PSIA").value,
          get(calcs) {
            return calcs.basicData.value.bottomTubingPressurePSIA;
          },
          getPath: () => "basicData.value.bottomTubingPressurePSIA",
          control(scenario, val) {
            return reactive({
              ...scenario,
              parameters: {
                ...scenario.parameters,
                __typename: "scenario_parameters",
                bottom_tubing_pressure_psia: val
              }
            });
          }
        },
        vfdLoadRatio: {
          label: "VSD Percent VFD Load (%)",
          base: PercentageUnits.Ratio,
          model: PercentageUnits.options(),
          target: PercentageUnits.Percentage,
          unitType: "PercentageUnits",
          getPath: () => "surfaceDriveCalculations.vfdLoadRatio.value",
          get(calcs) {
            return calcs.surfaceDriveCalculations.vfdLoadRatio.value;
          }
        },
        waterCutPercentage: {
          label: "Water Cut",
          unitType: "PercentageUnits",
          target: overrideUnits.value.PercentageUnits,
          base: PercentageUnits.Percentage,
          model: PercentageUnits.options(),
          getPath: () => "wellTestFlowRates.value.waterCutPercentage",
          get(calcs) {
            return convert(
              calcs.wellTestFlowRates.value.waterCutRatio,
              PercentageUnits.Ratio,
              PercentageUnits.Percentage
            );
          },
          control(scenario, val) {
            return reactive({
              ...scenario,
              wellProperties: {
                ...scenario.wellProperties,
                __typename: "scenario_well_properties",
                ...extrapolateFlowRates(
                  wellPropertiesWithAdjustedInputTypes(scenario),
                  "waterCutPercentage",
                  val
                )
              }
            });
          }
        },
        waterFlowRateSTBD: {
          label: "Water Rate",
          base: LiquidFlowRateUnits.BarrelsPerDay,
          model: LiquidFlowRateUnits.options(),
          unitType: "LiquidFlowRateUnits",
          target:
            overrideUnits.value.LiquidFlowRateUnits ||
            scenarioUnits.getLiquidFlowUnit("Water Rate.convert.STB/D").value,
          get(calcs) {
            if (settings.predictionFunction === predictDesiredFlowRate) {
              return calcs.surfaceFlowRates.value.waterFlowRateSTBD;
            } else {
              return calcs.wellTestFlowRates.value.waterFlowRateSTBD;
            }
          },
          getPath() {
            if (settings.predictionFunction === predictDesiredFlowRate) {
              return "surfaceFlowRates.value.waterFlowRateSTBD";
            } else {
              return "wellTestFlowRates.value.waterFlowRateSTBD";
            }
          }
        },
        wellFlowingPressureAtDatumPSIA: {
          label: "Well Flowing Pressure at Datum",
          base: PressureUnits.PoundsPerSquareInchAbsolute,
          model: PressureUnits.options(),
          unitType: "PressureUnits",
          target: overrideUnits.value.PressureUnits,
          get(calcs) {
            return calcs.wellFlowingPressureAtDatumPSIA.value;
          },
          getPath: () => "wellFlowingPressureAtDatumPSIA.value"
        },
        totalSystemEfficiencyRatio: {
          label: "System Efficiency",
          base: PercentageUnits.Ratio,
          model: PercentageUnits.options(),
          target: PercentageUnits.Percentage,
          unitType: "PercentageUnits",
          get(calcs) {
            return calcs.surfaceDriveCalculations.totalSystemEfficiencyRatio
              .value;
          },
          getPath: () =>
            "surfaceDriveCalculations.totalSystemEfficiencyRatio.value"
        },
        cableEfficiencyRatio: {
          label: "Cable Efficiency",
          base: PercentageUnits.Ratio,
          model: PercentageUnits.options(),
          target: PercentageUnits.Percentage,
          unitType: "PercentageUnits",
          get(calcs) {
            return calcs.surfaceDriveCalculations.cableEfficiencyRatio.value;
          },
          getPath: () => "surfaceDriveCalculations.cableEfficiencyRatio.value"
        },
        sealEfficiencyRatio: {
          label: "Seal Efficiency",
          base: PercentageUnits.Ratio,
          model: PercentageUnits.options(),
          target: PercentageUnits.Percentage,
          unitType: "PercentageUnits",
          get(calcs) {
            return calcs.surfaceDriveCalculations.sealEfficiencyRatio.value;
          },
          getPath: () => "surfaceDriveCalculations.sealEfficiencyRatio.value"
        },
        pumpEfficiencyRatio: {
          label: "Pump Efficiency",
          base: PercentageUnits.Ratio,
          model: PercentageUnits.options(),
          target: PercentageUnits.Percentage,
          unitType: "PercentageUnits",
          get(calcs) {
            return calcs.surfaceDriveCalculations.pumpEfficiencyRatio.value;
          },
          getPath: () => "surfaceDriveCalculations.pumpEfficiencyRatio.value"
        },
        viscosityRateCorrection: {
          label: "Pump(s) Viscosity Rate Correction",
          base: PercentageUnits.Percentage,
          model: [PercentageUnits.Percentage],
          unitType: "PercentageUnits",
          target: PercentageUnits.Percentage,
          get(calcs) {
            // Check if there's at least one pump with rate correction in the calculations
            const pumpCalculations =
              calcs.pumpCalculations?.individual?.value || {};
            const pumpIds = Object.keys(pumpCalculations);

            if (pumpIds.length === 0) return 100; // Default to 100% if no pumps

            // Get rate correction from the first pump's calculations
            const firstPumpId = pumpIds[0];
            const rateCorrection =
              pumpCalculations[firstPumpId]?.xrefPump.rate_correction_ratio ||
              1.0;
            return convert(
              rateCorrection,
              PercentageUnits.Ratio,
              PercentageUnits.Percentage
            );
          },
          getPath: () => "pumps.*.rate_correction_ratio",
          control(scenario, val) {
            // Convert from percentage to ratio
            const ratio = convert(
              val,
              PercentageUnits.Percentage,
              PercentageUnits.Ratio
            );

            // Create a copy of the pumps object
            const updatedPumps = { ...scenario.pumps };

            // Update all pumps with the new ratio
            Object.keys(updatedPumps).forEach(pumpId => {
              updatedPumps[pumpId] = {
                ...updatedPumps[pumpId],
                rate_correction_ratio: ratio
              };
            });

            return reactive({
              ...scenario,
              pumps: updatedPumps
            });
          }
        },
        // For viscosityHeadCorrection
        viscosityHeadCorrection: {
          label: "Pump(s) Viscosity Head Correction",
          base: PercentageUnits.Percentage,
          model: [PercentageUnits.Percentage],
          unitType: "PercentageUnits",
          target: PercentageUnits.Percentage,
          get(calcs) {
            const pumpCalculations =
              calcs.pumpCalculations?.individual?.value || {};
            const pumpIds = Object.keys(pumpCalculations);

            if (pumpIds.length === 0) return 100;

            const firstPumpId = pumpIds[0];
            const headCorrection =
              pumpCalculations[firstPumpId]?.xrefPump.head_correction_ratio ||
              1.0;
            return convert(
              headCorrection,
              PercentageUnits.Ratio,
              PercentageUnits.Percentage
            );
          },
          getPath: () => "pumps.*.head_correction_ratio",
          control(scenario, val) {
            // Convert from percentage to ratio
            const ratio = convert(
              val,
              PercentageUnits.Percentage,
              PercentageUnits.Ratio
            );

            // Create a copy of the pumps object
            const updatedPumps = { ...scenario.pumps };

            // Update all pumps with the new ratio
            Object.keys(updatedPumps).forEach(pumpId => {
              updatedPumps[pumpId] = {
                ...updatedPumps[pumpId],
                head_correction_ratio: ratio
              };
            });

            return reactive({
              ...scenario,
              pumps: updatedPumps
            });
          }
        },
        // For viscosityBHPCorrection
        viscosityBHPCorrection: {
          label: "Pump(s) Viscosity BHP Correction",
          base: PercentageUnits.Percentage,
          model: [PercentageUnits.Percentage],
          unitType: "PercentageUnits",
          target: PercentageUnits.Percentage,
          get(calcs) {
            const pumpCalculations =
              calcs.pumpCalculations?.individual?.value || {};
            const pumpIds = Object.keys(pumpCalculations);

            if (pumpIds.length === 0) return 100;

            const firstPumpId = pumpIds[0];
            const bhpCorrection =
              pumpCalculations[firstPumpId]?.xrefPump
                .brake_horsepower_correction_ratio || 1.0;
            return convert(
              bhpCorrection,
              PercentageUnits.Ratio,
              PercentageUnits.Percentage
            );
          },
          getPath: () => "pumps.*.brake_horsepower_correction_ratio",
          control(scenario, val) {
            // Convert from percentage to ratio
            const ratio = convert(
              val,
              PercentageUnits.Percentage,
              PercentageUnits.Ratio
            );

            // Create a copy of the pumps object
            const updatedPumps = { ...scenario.pumps };

            // Update all pumps with the new ratio
            Object.keys(updatedPumps).forEach(pumpId => {
              updatedPumps[pumpId] = {
                ...updatedPumps[pumpId],
                brake_horsepower_correction_ratio: ratio
              };
            });

            return reactive({
              ...scenario,
              pumps: updatedPumps
            });
          }
        }
      };
    })()
  );

  if (isHps.value) {
    if (analysisVariables.wellTestLiquidRateStbD)
      analysisVariables.wellTestLiquidRateStbD.label = liquidRate;
    if (analysisVariables.wellTestOilRateStbD)
      analysisVariables.wellTestOilRateStbD.label = oilRate;
    if (analysisVariables.wellTestWaterRateStbD)
      analysisVariables.wellTestWaterRateStbD.label = waterRate;
    if (analysisVariables.sealBearingLoadRatio)
      analysisVariables.sealBearingLoadRatio.label = thrustLoadBearingRatio;
    // your going to add the 3 new control variables here
    if (analysisVariables.pumpIntakePressurePsia) {
      const v = analysisVariables.pumpIntakePressurePsia as AnalysisVariable;
      v.label = "HPS Pump Inlet Pressure";
      v.control = (scenario, val) =>
        reactive({
          ...scenario,
          wellProperties: {
            ...scenario.wellProperties,
            __typename: "scenario_well_properties",
            hps_pump_inlet_pressure_psia: val
          }
        });
    }
    if (analysisVariables.pumpIntakeTemperatureF) {
      const v = analysisVariables.pumpIntakeTemperatureF as AnalysisVariable;
      v.label = "Inlet Temperature";
      v.control = (scenario, val) =>
        reactive({
          ...scenario,
          wellProperties: {
            ...scenario.wellProperties,
            __typename: "scenario_well_properties",
            bottomhole_temperature_fahrenheit: val
          }
        });
    }
    if (analysisVariables.requiredDischargePressurePSIA) {
      const v =
        analysisVariables.requiredDischargePressurePSIA as AnalysisVariable;
      v.label = "HPS Required Discharge Pressure";
      v.control = (scenario, val) =>
        reactive({
          ...scenario,
          wellProperties: {
            ...scenario.wellProperties,
            __typename: "scenario_well_properties",
            hps_required_discharge_pressure_psia: val
          }
        });
    }
  }
  // Sort the analysis variables by their label
  const sortedAnalysisVariables = Object.fromEntries(
    Object.entries(analysisVariables).sort(([, varA], [, varB]) => {
      return varA.label.localeCompare(varB.label);
    })
  );

  return sortedAnalysisVariables;
}

/**
 * All Analysis variables are "watch" variables. An analysis variable is a "control" variable when it
 * is defined with a control() function. The control() function should accept a number value as an argument and will
 * return a copy of the scenario which can be directly passed into the calculation composable.
 *
 * An example of the control function looks like this:
 *
 *  control(scenario, val) {
 *    return reactive({
 *      ...scenario,
 *      parameters: {
 *        ...scenario.parameters,
 *        __typename: "scenario_parameters",
 *        pump_hz: val
 *      }
 *    });
 *  }
 *
 * A few things to note:
 * - DestructuredScenario is a flattened map of the Scenario object that is returned from the Scenario query
 * - DestructuredScenario uses maps instead of arrays for Many to One related equipment (pumps, intakes, etc)
 * - We must spread the existing destructuredScenario object at each level where we modify properties
 * - We must wrap the whole object in a reactive() function in order to unwrap nested refs
 */

const onUnitSelected = (unitType?: string, unit?: ConvertableUnit) => {
  if (!unitType) return;
  if (!unit) {
    delete overrideUnits.value[unitType];
    return;
  }
  overrideUnits.value[unitType] = unit;
};

const analysisVariables = ref(makeAnalysisVariables());

watch(
  [scenario, overrideUnits],
  () => {
    analysisVariables.value = makeAnalysisVariables();
  },
  { deep: true }
);
// #endregion

watch(scenarioAnalysisMode, mode => {
  if (mode === "indiscrete" && controls.value.length > 4) {
    controls.value.splice(4); // keep the first four, discard the rest
  }
  // Reset analysis data
  resetAnalysis();
  controlVariableAnalysisData.value = makeInitialAnalysisData();
});

// #region Form Inputs
type Control = {
  selectedControl: AnalysisVariable | null;
  min: number;
  max: number;
  steps: number | null;
  reversed: boolean;
};

const showSettings = ref(false);
const settings = reactive<{
  liquidInputType?: Well_Test_Liquid_Input_Options_Enum | null;
  gasInputType?: Well_Test_Gas_Input_Options_Enum | null;
  predictionFunction: PredictionFn | null;
}>({
  liquidInputType:
    destructuredScenario.wellProperties.value?.well_test_liquid_input_type,
  gasInputType:
    destructuredScenario.wellProperties.value?.well_test_gas_input_type,
  predictionFunction: predictPumpFrequency
});

const wellPropertiesWithAdjustedInputTypes = (
  scenario: UnwrapNestedRefs<DestructuredScenario>
): WellProperties => {
  if (scenario.wellProperties) {
    return {
      ...scenario.wellProperties,
      well_test_gas_input_type: settings.gasInputType,
      well_test_liquid_input_type: settings.liquidInputType
    };
  }

  return scenario.wellProperties;
};

const DISABLE_PI_MODE_CONTROL_VARIABLES = ["PBHP", "Input Liquid Rate"];
const DISABLE_PI_MODE_WATCH_VARIABLES = ["PBHP"];

const wellPropertiesForm = injectStrict(wellPropertiesFormKey);
const wellTestInputType = computed(
  () => wellPropertiesForm.data.value?.well_test_liquid_input_type
);
watch(wellTestInputType, () => {
  settings.liquidInputType = wellTestInputType.value;
});

const isInPiMode = computed<boolean>(
  () => settings.liquidInputType === "productivity_index_water_cut"
);

const isVariableControlEnabled = (variable: AnalysisVariable): boolean => {
  if (!("control" in variable)) return false;
  if (isInPiMode.value) {
    return !DISABLE_PI_MODE_CONTROL_VARIABLES.includes(variable.label);
  }
  if (isHps.value && HPS_DISABLED_CONTROL_LABELS.includes(variable.label)) {
    return false;
  }
  return variable.label !== "PI";
};
const isVariableWatchEnabled = (variable: AnalysisVariable): boolean => {
  if (selectedControlVarLookup.value[variable.label]) return false;
  if (
    isInPiMode.value &&
    DISABLE_PI_MODE_WATCH_VARIABLES.includes(variable.label)
  ) {
    return false;
  }
  // HPS rules
  if (isHps.value) {
    if (HPS_DISABLED_WATCH_LABELS.includes(variable.label)) return false;
    const t = variable.ofType?.diff || "";
    if (
      t.startsWith(TypeNamesForAnalysis.TYPEHSSEAL) ||
      t.startsWith(TypeNamesForAnalysis.TYPESTANDARDSEAL) ||
      t.startsWith(TypeNamesForAnalysis.TYPEHSPUMP) ||
      t.startsWith(TypeNamesForAnalysis.TYPESTANDARDPUMP) ||
      t.startsWith(TypeNamesForAnalysis.TYPEHSINTAKE) ||
      t.startsWith(TypeNamesForAnalysis.TYPESTANDARDINTAKE)
    ) {
      return false;
    }
  }
  return true;
};

const controlVariables = computed<AnalysisVariable[]>(() =>
  Object.values(analysisVariables.value).filter(isVariableControlEnabled)
);

const controls = ref<Control[]>([]);

watch(controlVariables, () => {
  controls.value = controls.value.map(c => {
    const selectedControl = controlVariables.value.find(
      c2 => c2.label === c.selectedControl?.label
    );
    if (selectedControl) {
      return {
        ...c,
        selectedControl
      };
    }
    return c;
  });
});
// might not need this since hps never chages on a sizings made it just incase
// watch(isHps, () => {
//   controls.value = controls.value.filter(
//     c =>
//       !c.selectedControl ||
//       !HPS_DISABLED_CONTROL_LABELS.includes(c.selectedControl.label)
//   );
//   if (!controls.value.length) addControl();
//   selectedWatchVariables.value = selectedWatchVariables.value.filter(
//     isVariableWatchEnabled
//   );
// });

const addControl = () => {
  controls.value.push({
    selectedControl: null,
    min: 0,
    max: 0,
    steps: null,
    reversed: false
  });
};

const removeControl = (index: number) => {
  controls.value.splice(index, 1);
};

addControl();

watch(isInPiMode, () => {
  controls.value = controls.value.filter(v => {
    if (!v.selectedControl) return true; // leave unselected control values...
    return isVariableControlEnabled(v.selectedControl);
  });
  if (!controls.value.length) {
    addControl(); // ensure at least 1 control is added at all times.
  }
  selectedWatchVariables.value = selectedWatchVariables.value.filter(
    isVariableWatchEnabled
  );
});

const selectedControls = computed(
  () =>
    controls.value.filter(c => c.selectedControl) as Array<
      Control & { selectedControl: AnalysisVariable }
    >
);

const selectedControlLabels = ref<string[]>([]);

watch(selectedControls, () => {
  const defaultPredictionFunctions = selectedControls.value
    .filter(({ selectedControl }) => selectedControl.defaultPredictionFn)
    .map(
      ({ selectedControl }) =>
        selectedControl.defaultPredictionFn as PredictionFn
    );

  const availableOptions = predictionFunctionOptions.filter(
    option => !defaultPredictionFunctions.includes(option.value)
  );

  switch (availableOptions.length) {
    case 0:
      settings.predictionFunction = null;
      break;
    case 1:
      settings.predictionFunction = defaultPredictionFunctions[0];
      break;
  }
  selectedControlLabels.value = selectedControls.value.map(
    s => s.selectedControl.label
  );
});
watch(selectedControlLabels, (newV, oldV) => {
  const removedSelectedControl = oldV.filter(v => !newV.includes(v));
  if (!removedSelectedControl.length) {
    return;
  }
  selectedWatchVariables.value = selectedWatchVariables.value.filter(
    v => !removedSelectedControl.includes(v.label)
  );
});

const readyControls = computed(() =>
  selectedControls.value.filter(c => c.min <= c.max && c.steps)
);
const watchVariables = computed<AnalysisVariable[]>(() => {
  return Object.values(analysisVariables.value).filter(variable => {
    // Exclude any variables that shouldn't be enabled for watching
    if (!isVariableWatchEnabled(variable)) {
      return false;
    }
    if (
      "ofType" in variable &&
      variable.ofType &&
      !(variable.ofType as OfType).isMainType
    ) {
      return false;
    }

    return true;
  });
});

const selectedWatchVariables = ref<AnalysisVariable[]>([]);

const controlValues = computed(() => {
  if (!useFullCrossProduct.value) {
    const steps = totalSteps.value;
    return selectedControls.value.map(control => {
      const { max, min, reversed } = control;
      if (steps && (min < max || min === max)) {
        const values = Array(steps);
        if (min === max) {
          // If min equals max, all values are the same
          for (let i = 0; i < steps; i++) {
            values[i] = min;
          }
        } else {
          for (let i = 0; i < steps; i++) {
            const stepSize = (max - min) / (steps - 1);
            if (reversed) {
              // For reversed order, start from max and go to min
              values[i] = max - i * stepSize;
            } else {
              // For normal order, start from min and go to max
              values[i] = min + i * stepSize;
            }
          }
        }
        return values;
      }
      return [];
    });
  }

  // For Indiscrete mode (full cross product)
  return selectedControls.value.map(control => {
    const { max, min, steps, reversed } = control;
    if (steps && (min < max || min === max)) {
      const values = Array(steps + 1);
      if (min === max) {
        // If min equals max, all values are the same
        for (let i = 0; i <= steps; i++) {
          values[i] = min;
        }
      } else {
        const stepSize = (max - min) / steps;
        for (let i = 0; i <= steps; i++) {
          if (reversed) {
            // For reversed order, start from max and go to min
            values[i] = max - i * stepSize;
          } else {
            // For normal order, start from min and go to max
            values[i] = min + i * stepSize;
          }
        }
      }
      return values;
    }
    return [];
  });
});

const formatVariableHeader = (variable: AnalysisVariable): string => {
  if (variable.target?.shortLabel || variable.base?.shortLabel) {
    if (
      variable.ofType?.diff === TypeNamesForAnalysis.FIRSTHSSEAL ||
      variable.ofType?.diff === TypeNamesForAnalysis.FIRSTSTANDARDSEAL ||
      variable.ofType?.diff === TypeNamesForAnalysis.FIRSTHSPUMP ||
      variable.ofType?.diff === TypeNamesForAnalysis.FIRSTSTANDARDPUMP ||
      variable.ofType?.diff === TypeNamesForAnalysis.FIRSTHSINTAKE ||
      variable.ofType?.diff === TypeNamesForAnalysis.FIRSTSTANDARDINTAKE
    ) {
      return variable.displayLabel + "(%)" || "";
    }
    return `${variable.label} (${
      variable.target ? variable.target.shortLabel : variable.base.shortLabel
    })`;
  }
  return variable.label ?? ""; // Ensure fallback is an empty string if variable.label is undefined
};

const finalWatchVariables = computed({
  get() {
    let variables = selectedWatchVariables.value.filter(v => {
      return !selectedControls.value.find(
        v2 => v2.selectedControl.label === v.label
      );
    });

    // Add selected controls to the final variables
    variables = [
      ...selectedControls.value.map(v => v.selectedControl),
      ...variables
    ];

    return variables as Array<AnalysisVariable>;
  },
  set(val) {
    const allVariables = makeAnalysisVariables();

    const hsSeal1Exists = val.some(
      v =>
        v.ofType?.diff === TypeNamesForAnalysis.FIRSTHSSEAL &&
        v.ofType.isMainType
    );
    if (hsSeal1Exists) {
      Object.values(allVariables).forEach(variable => {
        const analysis = variable as AnalysisVariable;
        if (
          analysis.ofType?.diff.startsWith(TypeNamesForAnalysis.TYPEHSSEAL) &&
          !val.some(v => v.ofType?.diff === analysis.ofType?.diff)
        ) {
          val.push(analysis);
        }
      });
    } else {
      val = val.filter(
        v =>
          !v.ofType?.diff.startsWith(TypeNamesForAnalysis.TYPEHSSEAL) ||
          v.ofType?.diff === TypeNamesForAnalysis.FIRSTHSSEAL
      );
    }

    const standardSeal1Exists = val.some(
      v =>
        v.ofType?.diff === TypeNamesForAnalysis.FIRSTSTANDARDSEAL &&
        v.ofType.isMainType
    );
    if (standardSeal1Exists) {
      Object.values(allVariables).forEach(variable => {
        const analysis = variable as AnalysisVariable;
        if (
          analysis.ofType?.diff.startsWith(
            TypeNamesForAnalysis.TYPESTANDARDSEAL
          ) &&
          !val.some(v => v.ofType?.diff === analysis.ofType?.diff)
        ) {
          val.push(analysis);
        }
      });
    } else {
      val = val.filter(
        v =>
          !v.ofType?.diff.startsWith(TypeNamesForAnalysis.TYPESTANDARDSEAL) ||
          v.ofType?.diff === TypeNamesForAnalysis.FIRSTSTANDARDSEAL
      );
    }

    const hsPump1Exists = val.some(
      v =>
        v.ofType?.diff === TypeNamesForAnalysis.FIRSTHSPUMP &&
        v.ofType.isMainType
    );
    if (hsPump1Exists) {
      Object.values(allVariables).forEach(variable => {
        const analysis = variable as AnalysisVariable;
        if (
          analysis.ofType?.diff.startsWith(TypeNamesForAnalysis.TYPEHSPUMP) &&
          !val.some(v => v.ofType?.diff === analysis.ofType?.diff)
        ) {
          val.push(analysis);
        }
      });
    } else {
      val = val.filter(
        v =>
          !v.ofType?.diff.startsWith(TypeNamesForAnalysis.TYPEHSPUMP) ||
          v.ofType?.diff === TypeNamesForAnalysis.FIRSTHSPUMP
      );
    }

    const standardPump1Exists = val.some(
      v =>
        v.ofType?.diff === TypeNamesForAnalysis.FIRSTSTANDARDPUMP &&
        v.ofType.isMainType
    );
    if (standardPump1Exists) {
      Object.values(allVariables).forEach(variable => {
        const analysis = variable as AnalysisVariable;
        if (
          analysis.ofType?.diff.startsWith(
            TypeNamesForAnalysis.TYPESTANDARDPUMP
          ) &&
          !val.some(v => v.ofType?.diff === analysis.ofType?.diff)
        ) {
          val.push(analysis);
        }
      });
    } else {
      val = val.filter(
        v =>
          !v.ofType?.diff.startsWith(TypeNamesForAnalysis.TYPESTANDARDPUMP) ||
          v.ofType?.diff === TypeNamesForAnalysis.FIRSTSTANDARDPUMP
      );
    }

    const hsIntake1Exists = val.some(
      v =>
        v.ofType?.diff === TypeNamesForAnalysis.FIRSTHSINTAKE &&
        v.ofType.isMainType
    );
    if (hsIntake1Exists) {
      Object.values(allVariables).forEach(variable => {
        const analysis = variable as AnalysisVariable;
        if (
          analysis.ofType?.diff.startsWith(TypeNamesForAnalysis.TYPEHSINTAKE) &&
          !val.some(v => v.ofType?.diff === analysis.ofType?.diff)
        ) {
          val.push(analysis);
        }
      });
    } else {
      val = val.filter(
        v =>
          !v.ofType?.diff.startsWith(TypeNamesForAnalysis.TYPEHSINTAKE) ||
          v.ofType?.diff === TypeNamesForAnalysis.FIRSTHSINTAKE
      );
    }

    // Handle Standard Intake variables
    const standardIntake1Exists = val.some(
      v =>
        v.ofType?.diff === TypeNamesForAnalysis.FIRSTSTANDARDINTAKE &&
        v.ofType.isMainType
    );
    if (standardIntake1Exists) {
      Object.values(allVariables).forEach(variable => {
        const analysis = variable as AnalysisVariable;
        if (
          analysis.ofType?.diff.startsWith(
            TypeNamesForAnalysis.TYPESTANDARDINTAKE
          ) &&
          !val.some(v => v.ofType?.diff === analysis.ofType?.diff)
        ) {
          val.push(analysis);
        }
      });
    } else {
      val = val.filter(
        v =>
          !v.ofType?.diff.startsWith(TypeNamesForAnalysis.TYPESTANDARDINTAKE) ||
          v.ofType?.diff === TypeNamesForAnalysis.FIRSTSTANDARDINTAKE
      );
    }

    selectedWatchVariables.value = [...val];
  }
});

/**
 * The selected control value is also selected by default as one of the watch variables
 */
watch(
  () => controls.value.map(c => c.selectedControl),
  (newVal, oldVal) => {
    newVal.forEach((v, i) => {
      if (!oldVal[i] || oldVal[i]?.label !== v?.label) {
        controls.value[i].min = 0;
        controls.value[i].max = 0;
        controls.value[i].steps = null;
      }
    });
  }
);

// For reversed checkbox
watch(
  () => controls.value.map(control => control.reversed),
  () => {
    // Only trigger refresh if there's already data displayed
    if (analysisData.value.length > 0) {
      // Clear caches to force recalculation
      crossProductsCache.value = {};

      // Reset analysis data
      resetAnalysis();
      controlVariableAnalysisData.value = makeInitialAnalysisData();

      // Trigger data reload on next tick
      nextTick(() => {
        handleScroll();
      });
    }
  }
);

function resetForm() {
  selectedWatchVariables.value = [];
  const prevControlsLength = controls.value.length;
  controls.value = [];
  addControl();
  if (prevControlsLength > 1) {
    addControl();
  }
}

const hasValidAmountOfSteps = computed(() => {
  if (useFullCrossProduct.value) {
    return selectedControls.value.every(v => v.steps);
  } else {
    return !!selectedControls.value[0]?.steps;
  }
});

const isReady = computed<boolean>(
  () =>
    selectedControls.value.length === controls.value.length &&
    !!selectedControls.value.length &&
    !!finalWatchVariables.value.length &&
    selectedControls.value.every(v => v.min <= v.max) &&
    hasValidAmountOfSteps.value
);
// #endregion

// #region Analysis Data
const controllers = ref<AbortController[]>([]);
const analysisData = ref<AnalysisStep[]>([]);
const controlVariableAnalysisData = ref<AnalysisStep[]>([]);
const computedAnalysisData = ref<AnalysisStep[]>([]);

const completedSteps = ref(0);

const analysisContainerRef = ref<HTMLDivElement | null>(null);
const analysisTableRef = ref<DataTable | null>(null);

const scrollRef = ref<HTMLDivElement | null>(null);
const scrollInnerRef = ref<HTMLDivElement | null>(null);
const outer = ref<HTMLDivElement | null>(null);

watch([analysisContainerRef, analysisTableRef], () => {
  if (!analysisContainerRef.value) return;
  if (!analysisTableRef.value) return;
  nextTick(() => {
    const datatableWrapperEl = getDataTableWrapperElement();
    if (!datatableWrapperEl) return;
    if (!scrollRef.value) return;
    adjustHorizontalScrollElements();
    const firstChild = datatableWrapperEl.children[0] as HTMLDivElement;
    scrollRef.value.onscroll = () => {
      if (!analysisContainerRef.value || !scrollRef.value) return;
      datatableWrapperEl.scrollLeft = scrollRef.value.scrollLeft;
      firstChild.style.transform = `translateX(-${scrollRef.value.scrollLeft}px)`;
    };
  });
});

const getAnalysisDataAtIndex = (index: number, crossProduct?: number[]) => {
  if (controlVariableAnalysisData.value[index] !== undefined) {
    return controlVariableAnalysisData.value[index];
  }
  crossProduct = crossProduct || getCrossProductAtIndex(index);
  const row = {} as AnalysisStep;

  selectedWatchVariables.value.forEach(r => {
    row[r.label] = null;
  });
  selectedControls.value.forEach((v, j) => {
    if (!crossProduct) return;
    const controlValue = crossProduct[j];
    row[v.selectedControl.label] = controlValue;
  });
  if (!isExporting.value) {
    controlVariableAnalysisData.value[index] = row;
  }
  return row;
};

const makeInitialAnalysisData = () => {
  const m = new Array(totalSteps.value).fill(undefined);
  return m;
};

// if all watch variables are selected controls no computations are needed
const needsComputations = computed(() => {
  return selectedWatchVariables.value.some(
    v =>
      !selectedControls.value.some(v2 => v2.selectedControl.label === v.label)
  );
});

watch(
  [settings, selectedControls, scenario],
  () => {
    computedAnalysisData.value.length = 0;
  },
  { deep: true }
);

watchDebounced(
  [selectedWatchVariables, controls],
  () => {
    nextTick(() => adjustHorizontalScrollElements());
  },
  { deep: true, debounce: 250 }
);

const totalTableHeight = computed(() => totalSteps.value * ITEM_SIZE);

watch(useFullCrossProduct, () => {
  resetAnalysis();
  computedAnalysisData.value.length = 0;
});

watchThrottled(
  [
    selectedWatchVariables,
    readyControls,
    settings,
    useFullCrossProduct,

    () => controls.value.map(control => control.reversed)
  ],
  async () => {
    resetAnalysis();
    if (
      isReady.value &&
      !analysisData.value.length &&
      !lazyLoaded.value.length &&
      totalSteps.value
    ) {
      nextTick(() => {
        const datatableWrapperRef = getDataTableWrapperElement();
        if (datatableWrapperRef) {
          datatableWrapperRef.style.height = `${
            totalTableHeight.value + getDataTableHeaderHeight() + 40 // adds buffer to end for scroll
          }px`;
        }
        handleScroll();
      });

      controlVariableAnalysisData.value = makeInitialAnalysisData();
      handleScroll();
    }
  },
  { throttle: 1000 }
);

async function requestAnalysisData(
  values: number[],
  signal: AbortSignal
): Promise<AnalysisStep> {
  if (signal.aborted) {
    throw new DOMException("Analysis cancelled", "AbortError");
  }

  let scenario = reactive(destructuredScenario);
  for (let i = 0; i < selectedControls.value.length; i++) {
    const control = selectedControls.value[i];
    const value = values[i];

    if (typeof control.selectedControl.control !== "function") {
      throw new DOMException(
        "Control variable does not contain a control function."
      );
    }
    scenario = control.selectedControl.control(scenario, value);
  }
  if (signal.aborted) {
    throw new DOMException("Analysis cancelled", "AbortError");
  }
  const targetLiquidRateVariable = analysisVariables.value.targetLiquidRateStbD;

  const pathPropToLookup: { [key: string]: string } =
    finalWatchVariables.value.reduce<{ [key: string]: string }>(
      (acc, variable) => {
        acc[variable.label] = variable.getPath();
        return acc;
      },
      {
        [targetLiquidRateVariable.label]: targetLiquidRateVariable.getPath()
      }
    );

  let predicted: { neededCalcs: AnalysisStep } | null;
  let neededCalcs: AnalysisStep = {};
  if (settings.predictionFunction) {
    predicted = await settings.predictionFunction(scenario, pathPropToLookup);
  } else {
    predicted = await queryCalculation(scenario, undefined, pathPropToLookup);
  }
  if (signal.aborted) {
    throw new DOMException("Analysis cancelled", "AbortError");
  }

  if (!predicted) {
    throw new DOMException(
      "Prediction was null but the signal was not aborted."
    );
  }

  ({ neededCalcs } = predicted);

  const targetLiquidRate = neededCalcs[targetLiquidRateVariable.label];

  const reduced = finalWatchVariables.value.reduce<AnalysisStep>(
    (acc, variable) => {
      acc[variable.label] = neededCalcs[variable.label];
      return acc;
    },
    {}
  );

  // Check if the target liquid rate is NaN and set all variables to NaN
  if (isNaN(targetLiquidRate as number)) {
    const controlVariableLabels = selectedControls.value.map(
      c => c.selectedControl.label
    );
    finalWatchVariables.value.forEach(variable => {
      if (!controlVariableLabels.includes(variable.label)) {
        reduced[variable.label] = NaN;
      }
    });
  }
  return reduced;
}

function resetAnalysis() {
  for (const controller of controllers.value) {
    controller.abort();
  }
  controllers.value.length = 0;
  cancelCalculations();
  analysisData.value.length = 0;
  crossProductsCache.value = {};
  completedSteps.value = 0;
  lazyLoaded.value.length = 0;
  controlVariableAnalysisData.value.length = 0;
  exportAbortController.value?.abort();
  isExporting.value = false;
  nextTick(() => adjustStickyTopElements());
}
const crossProductsCache = ref<Record<number, number[]>>({});

const lazyLoaded = ref<boolean[]>([]);

const selectedControlVarLookup = computed(() => {
  return selectedControls.value.reduce(
    (acc, c) => {
      acc[c.selectedControl.label] = true;
      return acc;
    },
    {} as Record<string, boolean>
  );
});

async function lazyLoadAnalysis(start: number, last: number): Promise<number> {
  const total = last + 1 - start;
  await Promise.all(
    Array.from({ length: total }, async (_, index) => {
      const i = start + index;
      if (
        !lazyLoaded.value[i] &&
        !analysisData.value[i] &&
        i < totalSteps.value
      ) {
        const crossProduct = getCrossProductAtIndex(i);
        lazyLoaded.value[i] = true;
        analysisData.value[i] = getAnalysisDataAtIndex(i, crossProduct);
        if (!needsComputations.value) {
          return;
        }

        const computed = computedAnalysisData.value[i];
        if (computed) {
          const alreadyComputed = selectedWatchVariables.value.filter(
            v => v.label in computed && !selectedControlVarLookup.value[v.label]
          );
          alreadyComputed.forEach(v => {
            analysisData.value[i][v.label] = computed[v.label];
          });
          if (alreadyComputed.length === selectedWatchVariables.value.length) {
            return;
          }
        }
        const controller = new AbortController();
        controllers.value.push(controller);
        try {
          const result = await requestAnalysisData(
            crossProduct,
            controller.signal
          );
          if (!controller.signal.aborted && result) {
            computedAnalysisData.value[i] = result;
            if (analysisData.value[i] === undefined) {
              analysisData.value[i] = getAnalysisDataAtIndex(i);
            }
            for (const label in result) {
              if (!selectedControlVarLookup.value[label]) {
                analysisData.value[i][label] = result[label];
              }
            }
          }
        } catch (error) {
          log.warn("error trying to resolve analysis", error);
        }
        removeArrayElement(controllers.value, controller);
      }
    })
  );
  return total;
}

async function predictDesiredFlowRate(
  scenario: UnwrapNestedRefs<DestructuredScenario>,
  neededCalcs: { [key: string]: string } = {}
) {
  return queryCalculation(
    scenario,
    "predictedDesiredFlowRateSTBD",
    neededCalcs
  );
}

async function predictPumpFrequency(
  scenario: UnwrapNestedRefs<DestructuredScenario>,
  neededCalcs: { [key: string]: string } = {}
) {
  return queryCalculation(scenario, "predictedPumpFrequencyHz", neededCalcs);
}

function getCrossProductAtIndex(index: number): number[] {
  if (crossProductsCache.value[index] !== undefined) {
    return crossProductsCache.value[index];
  }
  const a = controlValues.value;
  if (a.length === 0) {
    return [];
  }
  if (useFullCrossProduct.value) {
    const result = [];
    let remainingIndex = index;
    for (let i = 0; i < a.length; i++) {
      const currentArray = a[i];
      const subProductSize = a
        .slice(i + 1)
        .reduce((acc, arr) => acc * arr.length, 1);

      const currentIndex = Math.floor(remainingIndex / subProductSize);
      remainingIndex %= subProductSize;

      result.push(currentArray[currentIndex]);
    }

    if (!isExporting.value) {
      crossProductsCache.value[index] = result;
    }
    return result;
  } else {
    return controlValues.value.map(v => {
      return v[index];
    });
  }
}

// #endregion

// #region CSS Bindings

const totalSteps: ComputedRef<number> = computed<number>(() => {
  if (useFullCrossProduct.value) {
    return controlValues.value.reduce((acc: number, cv: number[]) => {
      acc = acc * cv.length;
      return acc;
    }, 1);
  } else {
    const steps = selectedControls.value?.[0]?.steps
      ? selectedControls.value[0].steps + 1
      : 0;
    return steps;
  }
});

const totalStepsIgnoreZerosAndFocusedControl = computed(() =>
  !useFullCrossProduct.value
    ? totalSteps.value
    : controlValues.value.reduce((acc, cv, index) => {
        // acc shouldnt be effected if the control input is focused or if the length is not set yet and it's 0.
        if (
          cv.length &&
          selectedControls.value[index]?.selectedControl?.label !==
            focusedControlLabel.value
        ) {
          acc = acc * cv.length;
        }
        return acc;
      }, 1)
);

const totalStepsIgnoreZeros = computed(() =>
  !useFullCrossProduct.value
    ? totalSteps.value
    : controlValues.value.reduce((acc, cv) => {
        // acc shouldnt be effected if the control input is focused or if the length is not set yet and it's 0.
        if (cv.length) {
          acc = acc * cv.length;
        }
        return acc;
      }, 1)
);

watch(
  controlValues,
  () => {
    crossProductsCache.value = {};
  },
  { deep: true }
);

const progressWidth = computed(() =>
  totalSteps.value
    ? `${(completedSteps.value / totalSteps.value) * 100}%`
    : "0%"
);

const progressOpacity = computed(() =>
  totalSteps.value && completedSteps.value === totalSteps.value ? "0" : "1"
);
// #endregion

// #region watch variable container scroll
const watchContainerRef = ref<HTMLDivElement | null>(null);

const footerHeight = 80;

const isScrollable = ref(false);

const adjustHorizontalScrollElements = () => {
  if (!componentIsActive.value) return;
  const datatableWrapperEl = getDataTableWrapperElement();
  if (!scrollRef.value) return;

  if (!datatableWrapperEl) {
    isScrollable.value = false;
    scrollRef.value.style.display = "none";
    return;
  }
  const outerWidth = outer.value?.getBoundingClientRect().width;
  if (!outerWidth) return;
  const watchTableWidth =
    watchContainerRef.value?.getBoundingClientRect().width;
  if (!watchTableWidth) return;

  const maxWidth = outerWidth - watchTableWidth;
  datatableWrapperEl.style.maxWidth = `${maxWidth}px`;
  scrollRef.value.style.maxWidth = `${maxWidth}px`;

  const tableWidth =
    datatableWrapperEl.getElementsByTagName("table")[0]?.getBoundingClientRect()
      .width || 0;

  if (tableWidth <= maxWidth) {
    scrollRef.value.style.display = "none";
    isScrollable.value = false;
  } else {
    isScrollable.value = true;
    scrollRef.value.style.display = "";
    if (scrollInnerRef.value) {
      scrollInnerRef.value.style.width = `${tableWidth}px`;
    }
  }
};

const adjustStickyTopElements = () => {
  if (!componentIsActive.value) return;
  const headerHeight = getHeaderHeight();
  if (!watchContainerRef.value) return;

  const watchTable = watchContainerRef.value.getElementsByClassName(
    "watch-table"
  )[0] as HTMLElement;
  if (!watchTable) return;

  const tableWrapper = watchContainerRef.value.getElementsByClassName(
    "p-datatable"
  )[0] as HTMLElement;
  if (!tableWrapper) return;
  tableWrapper.style.top = `${headerHeight}px`;

  const maxHeight = window.innerHeight - headerHeight - footerHeight - 30;
  watchTable.style.maxHeight = `${maxHeight}px`;

  const datatableWrapperEl = getDataTableWrapperElement();
  const theads = datatableWrapperEl
    ? Array.from(datatableWrapperEl.getElementsByTagName("th"))
    : [];
  theads.forEach(h => {
    h.style.top = `${headerHeight - 1}.px`;
  });
};
// #endregion

// #region datatable feed scroll handling

const getHeaderHeight = () => {
  return (
    document.getElementById("scenario-header-wrapper")?.clientHeight || 150
  );
};

const scrollTop = ref(0);
const topVisibleIndex = ref(0);
const bottomVisibleIndex = ref(SCROLL_PAGE_SIZE);

const getDataTableWrapperElement = () => {
  if (analysisContainerRef.value === null) return;
  return analysisContainerRef.value.getElementsByClassName(
    "p-datatable"
  )[0] as HTMLDivElement;
};

const getDataTableHeaderHeight = () => {
  const header =
    analysisContainerRef?.value?.getElementsByClassName("p-datatable-thead")[0];
  return header?.clientHeight || 0;
};

const handleScroll = () => {
  if (!componentIsActive.value) return;
  if (analysisContainerRef.value === null) return;
  const tbody: HTMLDivElement =
    analysisContainerRef.value.getElementsByTagName("tbody")[0];
  if (!tbody) return;
  const { top } = tbody.getBoundingClientRect();
  // const theadPadding = getDataTableHeaderHeight();
  const itemBuffer = 3;
  // not at end of page but might have clicked a new watch variable while scrolled so need to find indexes

  const headerHeight = getHeaderHeight();
  const listStart = Math.abs(Math.min(top - headerHeight, 0));
  const listEnd = listStart + window.outerHeight - headerHeight;

  topVisibleIndex.value = Math.floor(listStart / ITEM_SIZE);

  bottomVisibleIndex.value = Math.max(
    Math.min(Math.ceil(listEnd / ITEM_SIZE), totalSteps.value - 1),
    topVisibleIndex.value
  );

  const startIndex = Math.max(
    Math.floor(listStart / ITEM_SIZE) - itemBuffer,
    0
  );

  scrollTop.value = topVisibleIndex.value * ITEM_SIZE;
  const endIndex = Math.max(
    Math.min(
      Math.ceil(listEnd / ITEM_SIZE) + itemBuffer * 2,
      totalSteps.value - 1
    ),
    0
  );
  lazyLoadAnalysis(startIndex, endIndex);
};
const handleResize = () => {
  adjustHorizontalScrollElements();
  adjustStickyTopElements();
};
const onScroll = useDebounceFn(handleScroll, 150);
const onResize = useDebounceFn(handleResize, 150);

watchEffect(() => {
  if (componentIsActive.value) {
    nextTick(() => {
      handleResize();
      handleScroll();
    });
  }
});

onMounted(() => {
  window.addEventListener("resize", onResize);
  window.addEventListener("scroll", adjustStickyTopElements);
  window.addEventListener("scroll", onScroll);
});

onUnmounted(() => {
  window.removeEventListener("resize", onResize);
  window.removeEventListener("scroll", adjustStickyTopElements);
  window.removeEventListener("scroll", onScroll);
});

// #endregion

const getConvertedValue = (value: AnalysisVariable) =>
  convert(value.get(scenarioCalculations), value.base, value.target);

const formatAnalysisSteps = (array: Array<AnalysisStep>) => {
  return array.map(data => {
    if (!data) return data;
    const copiedData: AnalysisStep = { ...data };
    finalWatchVariables.value.forEach(variable => {
      if (copiedData[variable.label]) {
        const decimalPlaces =
          variable.label === "PI"
            ? 4
            : variable.label === "Motor Power Factor"
              ? 3
              : 2;
        copiedData[variable.label] = formatNumber(
          convert(copiedData[variable.label], variable.base, variable.target),
          decimalPlaces
        );
      }
    });
    return copiedData as AnalysisStep;
  });
};
const analysisDisplayData = computed(() => {
  const total = topVisibleIndex.value - bottomVisibleIndex.value + 1;
  const array = formatAnalysisSteps(
    analysisData.value.slice(
      topVisibleIndex.value,
      bottomVisibleIndex.value + 1
    )
  );
  while (array.length < total) {
    array.push({});
  }
  return [{ isFirst: true }, ...array, { isLast: true }];
});

watchThrottled(
  analysisDisplayData,
  () => {
    nextTick(() => adjustHorizontalScrollElements());
  },
  { throttle: 500 }
);

// #region exporting analysis table data
type ExportFileTypes = "XLSX" | "CSV" | "PDF";
const exportFileTypes = ref<ExportFileTypes[]>(["XLSX", "CSV", "PDF"]);
const exportFileType = ref<ExportFileTypes>("XLSX");

const exportAbortController = ref<AbortController>();
const isExporting = ref(false);
const exportProgress = ref(0);

const exportTableFileName = computed(() => {
  return `Analysis  ${new Date()
    .toLocaleDateString()
    .replaceAll("/", "-")}.${exportFileType.value.toLowerCase()}`;
});

const workbook = ref<ReturnType<typeof XLSX.utils.book_new> | null>(null);
const worksheet = ref<ReturnType<typeof XLSX.utils.aoa_to_sheet> | null>(null);

const startTableExport = (headerRow: (string | number | null)[]) => {
  workbook.value = XLSX.utils.book_new();
  worksheet.value = XLSX.utils.aoa_to_sheet([headerRow]);
};

const addToTable = (sheetData: (string[] | (number | null)[])[]) => {
  XLSX.utils.sheet_add_aoa(worksheet.value, sheetData, {
    origin: -1
  });
};

const finalizeTableExport = () => {
  XLSX.utils.book_append_sheet(workbook.value, worksheet.value, "Sheet 1");
  XLSX.writeFile(workbook.value, exportTableFileName.value);
};

const exportPdfFile = (sheetData: (string[] | (number | null)[])[]) => {
  if (!sheetData.length) return;

  let firstTableData: TableCell[][] = sheetData as TableCell[][];
  const secondTableData: TableCell[][] = [];

  const maxColumns = 19;

  let fontSize = 4;

  const headerRow = sheetData[0];
  if (headerRow.length > maxColumns) {
    const controlVarLen = selectedControls.value.length;
    firstTableData = [];
    // Split data into two separate arrays for the first and second tables
    sheetData.forEach(rowData => {
      const controls = rowData.slice(0, controlVarLen);
      const firstTableDataRow = rowData.slice(
        controlVarLen,
        controlVarLen + maxColumns
      );
      const secondTableDataRow = rowData.slice(controlVarLen + maxColumns);
      firstTableData.push([
        ...(controls as TableCell[]),
        ...(firstTableDataRow as TableCell[])
      ]);
      secondTableData.push([
        ...(controls as TableCell[]),
        ...(secondTableDataRow as TableCell[])
      ]);
    });
  } else if (headerRow.length < 9) {
    fontSize = 8;
  }

  const content: Content = [
    {
      table: {
        headerRows: 1,
        body: firstTableData
      }
    }
  ];
  if (secondTableData.length) {
    content.push({ text: "\n\n" });
    content.push({
      table: {
        headerRows: 1,
        body: secondTableData
      }
    });
  }

  const pdf = pdfMake.createPdf(
    {
      pageSize: "A5",
      pageMargins: 1,
      pageOrientation: "landscape",
      content,
      defaultStyle: {
        fontSize
      }
    },
    undefined,
    undefined,
    pdfFonts
  );

  pdf.download(exportTableFileName.value);
};

const formatExportData = (startIndex: number, endIndex: number) => {
  return formatAnalysisSteps(
    analysisData.value.slice(startIndex, endIndex + 1)
  ).map(data => {
    return finalWatchVariables.value.map(variable => {
      const rowVal = data[variable.label];
      if (rowVal === undefined || isNaN(rowVal as number)) return null;

      return rowVal;
    });
  });
};

const exportAnalysisData = () => {
  if (exportFileType.value !== "PDF") {
    finalizeTableExport();
    return;
  }
  const sheetData = [
    finalWatchVariables.value.map(variable => formatVariableHeader(variable)),
    ...formatAnalysisSteps(analysisData.value).map(data => {
      return finalWatchVariables.value.map(variable => {
        const rowVal = data[variable.label];
        if (rowVal === undefined || isNaN(rowVal as number)) return null;

        return rowVal;
      });
    })
  ];
  exportPdfFile(sheetData);
};

const exportError = ref<string | null>(null);
const exportTotal = ref<string>("0");
const exportFinished = ref<number>(0);

const startExport = async () => {
  if (exportAbortController.value) {
    exportAbortController.value.abort();
  }

  exportError.value = null;
  isExporting.value = true;
  exportProgress.value = 0;
  const batchSize = 16;

  const persistEveryNBatches = 10;

  const total = totalSteps.value;
  const lastIndex = total - 1;
  let curBatch = 0;
  // do this in batches so the process doesnt get tied up preventing vue from being reactive and will allow us to update the ui with loading progress
  const abortController = new AbortController();
  exportAbortController.value = abortController;
  exportTotal.value = total.toLocaleString();
  exportFinished.value = 0;
  let cancelled = false;
  let error = null;
  let lastExportEndIndex = 0;

  const isTableExport = exportFileType.value !== "PDF";

  if (isTableExport) {
    startTableExport(
      finalWatchVariables.value.map(variable => formatVariableHeader(variable))
    );
  }

  let batchesComplete = 0;

  while (curBatch <= lastIndex && !cancelled && !error) {
    isExporting.value = true;
    if (curBatch !== 0) {
      await asyncTimeout(5);
      if (abortController.signal.aborted) {
        cancelled = true;
        break;
      }
    }
    try {
      const loaded = await lazyLoadAnalysis(
        curBatch,
        Math.min(curBatch + batchSize - 1, lastIndex)
      );
      if (abortController.signal.aborted) {
        cancelled = true;
        break;
      }

      curBatch += batchSize;
      exportFinished.value += loaded;

      const prog = Math.min(exportFinished.value / total, 1) * 100;
      exportProgress.value =
        total > 200 ? parseFloat(prog.toFixed(1)) : Math.ceil(prog);
      batchesComplete++;
      if (
        isTableExport &&
        (!(batchesComplete % persistEveryNBatches) ||
          exportFinished.value >= total)
      ) {
        const startIdx = lastExportEndIndex;
        const itemBuffer = 3;
        const endIdx = curBatch - 1;
        addToTable(formatExportData(startIdx, endIdx));
        for (let i = startIdx; i <= endIdx; i++) {
          if (
            topVisibleIndex.value - itemBuffer <= i &&
            i <= bottomVisibleIndex.value + itemBuffer
          ) {
            continue;
          }
          delete analysisData.value[i];
        }
        lastExportEndIndex = endIdx + 1;
        // wait just a bit so garbage collection can do its thing
        await asyncTimeout(100);
      }
    } catch (e: unknown) {
      error =
        typeof e === "object" && e !== null && "message" in e
          ? (e.message as string)
          : "An error occurred while exporting the analysis data";
      console.error(error);
    }
  }

  isExporting.value = false;
  if (cancelled) {
    return;
  }
  if (error) {
    exportError.value = error;
  } else {
    exportAnalysisData();
  }
};

const cancelExport = () => {
  isExporting.value = false;
  exportAbortController.value?.abort();
};

const maxHeightAllowedInBrowser = 5_164_000;
const maxRows = Math.floor(maxHeightAllowedInBrowser / ITEM_SIZE);

// this is used so when you focus an input you can set the max steps to the
// remaining capacity as if the current input was at 0
const maxStepsExcludeFocusedInput = computed(() => {
  const remainingCapacity = Math.floor(
    maxRows / totalStepsIgnoreZerosAndFocusedControl.value
  );
  return Math.min(100, remainingCapacity);
});

// dont want to be able to add a control until the focused input
// is blurred and can confirm the limit isnt over.
const maxStepsIncludeFocusedInput = computed(() => {
  const remainingCapacity = Math.floor(maxRows / totalStepsIgnoreZeros.value);
  return Math.min(100, remainingCapacity);
});

const canAddAnotherControl = computed(() => {
  return (
    controls.value.length < maxControlsAllowed.value &&
    maxStepsIncludeFocusedInput.value > 0
  );
});

// #endregion
</script>

<style lang="scss" scoped>
.table-loader {
  width: 100%;
  height: 230px;
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
}
.scenario-analysis {
  margin-bottom: 1em;
}

.header-container {
  display: flex;
  gap: 1em;
  flex-direction: column;
  justify-content: space-between;
}

.analysis-modes {
  display: flex;
  align-items: center;
  flex-direction: column;
  > div {
    > span {
      &:not(:last-of-type) {
        margin-right: 1em;
      }
    }
  }
}

.reverse-checkbox {
  display: flex;
  align-items: center;
  gap: 0.5em;
  margin-top: 0.5em;
  font-size: 0.8em;
  color: var(--gray-700);
  margin-left: 2px;
}

.export-button {
  margin-right: 10px;
}

.export-progress {
  display: flex;
  flex-direction: row;
  align-items: center;
  .progress-labels {
    min-width: 50px;
    width: 50px;
    display: flex;
    flex-direction: column;
    .progress-completed {
      font-size: 0.5em;
      color: var(--gray-600);
    }
  }
  .progressbar-wrapper {
    margin-right: 10px;
    margin-left: 10px;
    width: 400px;
    min-width: 400px;
  }
  #export-pro {
    position: absolute;
  }
  &:not(.animate) {
    :deep(.p-progressbar-determinate .p-progressbar-value-animate) {
      -webkit-transition: none !important;
      transition: none !important;
    }
  }
}

.stepper-input {
  visibility: hidden;
  &.stepper-available {
    visibility: visible;
  }
}

.add-remove-button-group {
  height: 100%;
  justify-content: start;
  width: 100%;
}

.data-container {
  display: grid;
  grid-template-columns: 25% 1fr;
  column-gap: 1em;
}

.data-buttons {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1em;
  white-space: nowrap;
  padding: 1em 0;
}

.watch-container {
  flex: 1;
  z-index: 1;
  .p-datatable {
    position: sticky;
    z-index: 9;
  }
}

.analysis-container {
  position: sticky;
  top: 112px;
  display: flex;
  flex-direction: column;
  row-gap: 0em;
  column-gap: 1em;
  flex: 3;
  flex-wrap: nowrap;
  overflow-x: clip;

  .scroller {
    overflow-x: scroll;
    position: sticky;
    height: 20px;
    z-index: 10;
    top: calc(100% - 20px);
  }
  :deep(.p-datatable-tbody .item) {
    height: 40px !important;
  }
  :deep(.p-datatable-tbody td) {
    box-shadow: 0 -1px 0 0 var(--border-main);
    border: none !important;
    padding-top: 16px !important;
    padding-bottom: 16px !important;
  }
  :deep(.p-datatable-tbody tr:last-child) {
    height: 0px;
    display: none;
  }
  :deep(thead) {
    z-index: 9;
    th {
      white-space: nowrap;
      position: sticky;
    }
  }
  :deep(.p-datatable) {
    overflow-x: clip;
  }

  &.scrollable {
    :deep(.p-datatable) {
      margin-top: -20px;
    }
    :deep(.p-datatable-tbody tr:last-child) {
      visibility: visible;
      height: 40px;
    }
  }
  :deep(.p-datatable-tbody tr:first-child) {
    border: 0;
    td {
      margin: 0px !important;
      padding: 0px !important;
    }
  }
}

.watch-table {
  border: 1px solid var(--border-main);
  overflow-y: scroll;
  :deep(.p-selection-column, .p-datatable-thead > th) {
    max-width: 3em;
  }

  :deep(.p-datatable-frozen-tbody) {
    tr.p-selectable-row {
      cursor: default;

      &.p-highlight {
        background: var(--gray-300) !important;
        color: var(--gray-600) !important;
      }

      .p-selection-column > .p-checkbox > .p-checkbox-box.p-highlight {
        cursor: default;

        background: var(--gray-600) !important;
        border-color: var(--gray-300) !important;
      }
    }
  }
}
.settings-container {
  background: var(--gray-50);
  border: 1px solid var(--gray-400);
  padding: 1em;

  display: grid;
  grid-template-rows: 1fr 1fr 1fr;
  row-gap: 2em;
  .input-option-group {
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: repeat(5, min-content);
    column-gap: 0;
    row-gap: 0.5em;

    @include bp("desktop") {
      column-gap: 2em;
      grid-template-rows: 1fr 1fr;
      grid-template-columns: repeat(4, min-content);

      > p.body-2 {
        grid-column: 1 / 5;
      }
    }

    > div {
      display: flex;
      align-items: center;
      gap: 0.5em;
      white-space: nowrap;
      font-size: 0.75em;
    }
  }
}

.analysis-placeholder {
  background: var(--gray-50);
  border: 1px solid var(--gray-400);
  padding: 0.5em;

  display: flex;
  justify-content: center;
  align-items: center;
  color: var(--gray-600);
}

.analysis-table {
  .first {
    background: repeating-linear-gradient(
      to bottom,
      var(--surface-25) 0,
      var(--surface-25) 49px,
      var(--border-main) 49px,
      var(--border-main) 50px
    );
    &::after {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      width: 100%; /* Full width of parent */
      height: 100%; /* 10x the height of parent (100% * 10) */
      background: repeating-linear-gradient(
        to bottom,
        var(--surface-25) 0,
        var(--surface-25) 49px,
        var(--border-main) 49px,
        var(--border-main) 50px
      );
      z-index: -1;
      pointer-events: none; /* Optional: so it doesn't interfere with clicks */
    }
  }
  :deep(.p-datatable-wrapper) {
    table {
      border: 1px solid var(--border-main);
    }
  }

  :deep(thead.p-datatable-thead) {
    position: relative;

    // Datatable Progress Bar
    &::after {
      content: " ";
      display: block;
      opacity: v-bind(progressOpacity);
      position: absolute;
      width: v-bind(progressWidth);
      height: 4px;
      bottom: -2px;
      background: var(--info-500);
      transition:
        width 300ms ease-out,
        opacity 1s ease-in-out;
    }
  }
}
</style>
